import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

// KickStart! – Klassens aktivitetsverktøy (uten Tailwind CSS)

// -----------------------------
// HJELPEFUNKSJONER & HOOKS
// -----------------------------
function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function beep(duration = 500, freq = 880) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.frequency.value = freq;
    o.connect(g);
    g.connect(ctx.destination);
    o.start();
    g.gain.exponentialRampToValueAtTime(0.00001, ctx.currentTime + duration / 1000);
    setTimeout(() => {
      o.stop();
      ctx.close();
    }, duration);
  } catch (e) {
    // no-op if audio blocked
  }
}

// Erstatter localStorage med React state + localStorage backup
function useAppStorage(key, initialValue) {
  const [value, setValue] = useState(() => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const item = window.localStorage.getItem(key);
        return item ? JSON.parse(item) : initialValue;
      }
    } catch (error) {
      console.warn('localStorage ikke tilgjengelig:', error);
    }
    return initialValue;
  });

  const setStoredValue = (newValue) => {
    try {
      setValue(newValue);
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem(key, JSON.stringify(newValue));
      }
    } catch (error) {
      console.warn('Kunne ikke lagre til localStorage:', error);
    }
  };

  return [value, setStoredValue];
}

// -----------------------------
// GLOBAL APP-STATE (Context)
// -----------------------------
const defaultState = {
  classes: [
    {
      id: "klasseliste-1",
      name: "klasse",
      students: ["Ola", "Kari", "Lise", "Lars"],
      teams: []
    },
  ],
  activeClassId: "klasseliste-1",
  theme: "lyst",   // 'lyst' | 'mørkt'
  ui: {
    railPanel: null,       // null | 'klasse' | 'timer' | 'poeng'
    view: "hub",           // 'hub' | 'activity'
    activeActivity: null,
    activityKey: 0         // bumped to force a remount when re-picking the same activity
  },
  draw: {
    pool: [],
    current: null,
    total: 0,
    mode: "student",       // 'student' | 'team'
    count: 1
  },
  stopwatch: {
    running: false,
    remaining: 60,
    duration: 60
  },
  scores: {
    enabled: false,
    teams: [
      { id: "t1", name: "Lag 1", score: 0 },
      { id: "t2", name: "Lag 2", score: 0 }
    ]
  }
};

// -----------------------------
// DESIGN TOKENS
// -----------------------------
const COLOR = {
  appBg: '#0a0c10',
  surfaceBg: 'var(--surface-bg)',
  chromeSurface: '#0E1116',    // always-dark: rail/panel-only boxes (draw field, panel inputs)
  railBg: 'var(--rail-bg)',
  panelBg: '#141920',          // panel stays dark regardless of theme
  card: 'var(--card)',
  cardHover: 'var(--card-hover)',
  cardAlt: 'var(--card-alt)',
  popover: 'var(--popover)',
  btnSecondary: 'var(--btn-secondary-bg)',
  btnSecondaryHover: 'var(--btn-secondary-hover)',
  activeIconBg: '#232935',
  track: 'var(--track)',
  miniBtn: '#252C38',
  trackLine: 'var(--track-line)',
  mutedText: 'var(--muted-text)',
  mutedText2: 'var(--muted-text-2)',
  accent: '#FFB020',
  accentHover: '#ffc24d',
  accentText: 'var(--accent-text)',   // theme-safe amber (brown on light paper)
  textPrimary: 'var(--text-primary)',
  textCard: 'var(--text-card)',
  textSecondary: 'var(--text-secondary)',
  textSecondaryOnPanel: '#98A1AF',
  railLabel: '#A7AFBC',
  chipText: 'var(--chip-text)',
  accentOnDark: '#0a0c10',
  chromeText: '#F2F4F7',      // always-light text on rail/panel chrome
  chromeSecondary: '#1F2530', // always-dark secondary fill for rail/panel chrome
  drawnPillBg: 'var(--drawn-pill-bg)',
  drawnPillBorder: 'var(--drawn-pill-border)',
  shellBorder: 'var(--shell-border)',
  btnSecondaryBorder: 'var(--btn-secondary-border)',
  btnSecondaryText: 'var(--btn-secondary-text)',
  backBorder: 'var(--back-border)',
  backText: 'var(--back-text)',
  badgeBg: 'var(--badge-bg)',
  badgeBorder: 'var(--badge-border)',
  badgeText: 'var(--badge-text)',
  badgeReqBg: 'var(--badge-req-bg)',
  badgeReqBorder: 'var(--badge-req-border)',
  badgeReqText: 'var(--badge-req-text)'
};

const FONT_DISPLAY = "'Bricolage Grotesque', sans-serif";
const FONT_UI = "'Instrument Sans', sans-serif";

function primaryBtnStyle(extra) {
  return {
    background: COLOR.accent,
    color: COLOR.accentOnDark,
    border: 'none',
    borderRadius: '12px',
    padding: '13px 26px',
    fontFamily: FONT_UI,
    fontWeight: 600,
    fontSize: '16px',
    cursor: 'pointer',
    transition: 'background 0.15s ease',
    ...extra
  };
}
function secondaryBtnStyle(extra) {
  return {
    background: COLOR.btnSecondary,
    color: COLOR.textCard,
    border: '1px solid rgba(255,255,255,.09)',
    borderRadius: '11px',
    padding: '11px 20px',
    fontFamily: FONT_UI,
    fontWeight: 600,
    fontSize: '15px',
    cursor: 'pointer',
    transition: 'background 0.15s ease',
    ...extra
  };
}
function quietBtnStyle(extra) {
  return {
    background: 'transparent',
    border: `1px solid ${COLOR.backBorder}`,
    color: COLOR.backText,
    borderRadius: '9px',
    padding: '7px 14px',
    fontFamily: FONT_UI,
    fontWeight: 500,
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'color 0.15s ease',
    ...extra
  };
}
function toggleOffBtnStyle(extra) {
  return {
    background: 'transparent',
    border: '1px dashed rgba(255,255,255,.16)',
    color: COLOR.textSecondaryOnPanel,
    borderRadius: '11px',
    padding: '13px 15px',
    fontFamily: FONT_UI,
    fontWeight: 500,
    fontSize: '15px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    transition: 'color 0.15s ease, border-color 0.15s ease',
    ...extra
  };
}
function linkBtnStyle(extra) {
  return {
    background: 'none',
    border: 'none',
    color: COLOR.textSecondary,
    textDecoration: 'underline',
    padding: 0,
    fontFamily: FONT_UI,
    fontSize: '13px',
    cursor: 'pointer',
    ...extra
  };
}
function miniBtnStyle(variant) {
  return {
    width: '25px',
    height: '25px',
    borderRadius: '7px',
    border: 'none',
    cursor: 'pointer',
    fontFamily: FONT_UI,
    fontWeight: 700,
    fontSize: '15px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: variant === 'plus' ? COLOR.accent : COLOR.miniBtn,
    color: variant === 'plus' ? COLOR.accentOnDark : COLOR.chromeText
  };
}
function panelLabelStyle(extra) {
  return {
    fontSize: '12px',
    fontFamily: FONT_UI,
    letterSpacing: '.09em',
    textTransform: 'uppercase',
    color: COLOR.textSecondaryOnPanel,
    ...extra
  };
}
function panelInputStyle(extra) {
  return {
    background: COLOR.chromeSurface,
    border: '1px solid rgba(255,255,255,.12)',
    borderRadius: '9px',
    padding: '10px 12px',
    color: COLOR.chromeText,
    fontFamily: FONT_UI,
    fontSize: '14px',
    outline: 'none',
    ...extra
  };
}

function mainPrimaryBtnStyle(extra) {
  return {
    background: COLOR.accent,
    color: COLOR.accentOnDark,
    border: 'none',
    borderRadius: '13px',
    padding: '15px 32px',
    fontFamily: FONT_UI,
    fontWeight: 600,
    fontSize: '19px',
    cursor: 'pointer',
    transition: 'background 0.15s ease',
    ...extra
  };
}
function mainSecondaryBtnStyle(extra) {
  return {
    background: COLOR.btnSecondary,
    color: COLOR.btnSecondaryText,
    border: `1px solid ${COLOR.btnSecondaryBorder}`,
    borderRadius: '13px',
    padding: '15px 32px',
    fontFamily: FONT_UI,
    fontWeight: 400,
    fontSize: '19px',
    cursor: 'pointer',
    transition: 'background 0.15s ease',
    ...extra
  };
}
function mainSecondaryBtnSmallStyle(extra) {
  return mainSecondaryBtnStyle({ borderRadius: '12px', padding: '13px 28px', fontSize: '17px', ...extra });
}
function introPrimaryBtnStyle(extra) {
  return {
    background: COLOR.accent,
    color: COLOR.accentOnDark,
    border: 'none',
    borderRadius: '12px',
    padding: '17px 40px',
    fontFamily: FONT_UI,
    fontWeight: 600,
    fontSize: '21px',
    cursor: 'pointer',
    transition: 'background 0.15s ease',
    ...extra
  };
}
function introSecondaryBtnStyle(extra) {
  return {
    background: 'transparent',
    color: COLOR.textSecondary,
    border: `1px solid ${COLOR.btnSecondaryBorder}`,
    borderRadius: '12px',
    padding: '17px 40px',
    fontFamily: FONT_UI,
    fontWeight: 600,
    fontSize: '21px',
    cursor: 'pointer',
    transition: 'border-color 0.15s ease',
    ...extra
  };
}
function mainFieldStyle(extra) {
  return {
    background: COLOR.surfaceBg,
    border: '1px solid rgba(255,255,255,.12)',
    borderRadius: '9px',
    padding: '11px 13px',
    color: COLOR.textPrimary,
    fontFamily: FONT_UI,
    fontSize: '15px',
    outline: 'none',
    ...extra
  };
}
function ActivityStatus({ status }) {
  if (!status) return null;
  if (typeof status === 'string') {
    return <span style={{ color: COLOR.textSecondary, fontSize: '15px', fontFamily: FONT_UI }}>{status}</span>;
  }
  return (
    <span style={{ fontSize: '15px', color: COLOR.textSecondary, fontFamily: FONT_UI }}>
      {status.label}{' '}
      <span style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '17px', color: COLOR.textPrimary }}>{status.value}</span>
      {status.suffix}
    </span>
  );
}

const AppStateContext = React.createContext(null);
const AppStateDispatchContext = React.createContext(null);

function AppStateProvider({ children }) {
  const [state, setState] = useAppStorage("kickstart_state", defaultState);
  return (
    <AppStateContext.Provider value={state}>
      <AppStateDispatchContext.Provider value={setState}>
        {children}
      </AppStateDispatchContext.Provider>
    </AppStateContext.Provider>
  );
}

function useAppState() {
  const s = React.useContext(AppStateContext);
  const d = React.useContext(AppStateDispatchContext);
  if (!s || !d) {
    throw new Error("AppStateProvider mangler. Pakk App i <AppStateProvider>.");
  }
  return [s, d];
}

// -----------------------------
// HOVEDAPP
// -----------------------------
export default function App() {
  return (
    <AppStateProvider>
      <ThemedShell />
    </AppStateProvider>
  );
}

function ThemedShell() {
  const [state] = useAppState();
  const theme = state.theme === "mørkt" ? "mørkt" : "lyst";
  return (
    <>
      <style dangerouslySetInnerHTML={{
        __html: `
          [data-theme="lyst"] {
            --surface-bg: #FBF7F0;
            --rail-bg: #1A1F28;
            --text-primary: #1E232B;
            --text-secondary: #5C6470;
            --text-card: #1E232B;
            --card: #FFFFFF;
            --card-hover: #F3EEE4;
            --card-alt: #F3EEE4;
            --popover: #FFFFFF;
            --muted-text: #8A8578;
            --muted-text-2: #A39D8E;
            --track-line: #D8CFC0;
            --chip-text: #1E232B;
            --track: #E6DFD3;
            --btn-secondary-bg: #FFFFFF;
            --btn-secondary-hover: #F3EEE4;
            --btn-secondary-border: rgba(0,0,0,.14);
            --btn-secondary-text: #1E232B;
            --back-border: rgba(0,0,0,.18);
            --back-text: #5C6470;
            --accent-text: #8F4506;
            --drawn-pill-bg: #FFFFFF;
            --drawn-pill-border: rgba(0,0,0,.12);
            --shell-border: rgba(0,0,0,.1);
            --badge-bg: rgba(0,0,0,.04);
            --badge-border: rgba(0,0,0,.1);
            --badge-text: #6B7280;
            --badge-req-bg: rgba(255,176,32,.18);
            --badge-req-border: rgba(143,69,6,.35);
            --badge-req-text: #8F4506;
          }
          [data-theme="mørkt"] {
            --surface-bg: #0E1116;
            --rail-bg: #12161D;
            --text-primary: #F2F4F7;
            --text-secondary: #8C95A4;
            --text-card: #E6E9EF;
            --card: #151A22;
            --card-hover: #1B212B;
            --card-alt: #171C25;
            --popover: #161B23;
            --muted-text: #5E6674;
            --muted-text-2: #4E5663;
            --track-line: #2A313D;
            --chip-text: #D5DAE3;
            --track: #1C222C;
            --btn-secondary-bg: #1A1F28;
            --btn-secondary-hover: #2A313D;
            --btn-secondary-border: rgba(255,255,255,.1);
            --btn-secondary-text: #E6E9EF;
            --back-border: rgba(255,255,255,.14);
            --back-text: #99A2B0;
            --accent-text: #FFB020;
            --drawn-pill-bg: #151A22;
            --drawn-pill-border: rgba(255,176,32,.3);
            --shell-border: rgba(255,255,255,.09);
            --badge-bg: rgba(255,255,255,.06);
            --badge-border: rgba(255,255,255,.1);
            --badge-text: #A7AFBC;
            --badge-req-bg: rgba(255,176,32,.14);
            --badge-req-border: rgba(255,176,32,.45);
            --badge-req-text: #FFCB63;
          }

          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }

          @keyframes ping {
            75%, 100% {
              transform: scale(2);
              opacity: 0;
            }
          }

          ::-webkit-scrollbar {
            width: 8px;
          }

          ::-webkit-scrollbar-track {
            background: rgba(255, 255, 255, 0.1);
            border-radius: 4px;
          }

          ::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.3);
            border-radius: 4px;
          }

          ::-webkit-scrollbar-thumb:hover {
            background: rgba(255, 255, 255, 0.5);
          }

          select option {
            background: #1f2937 !important;
            color: white !important;
          }

          *:focus-visible {
            outline: 2px solid ${COLOR.accent};
            outline-offset: 2px;
          }

          .ks-intro-wrap { padding: 0 90px 50px; }
          .ks-intro-name { font-size: 64px; }
          .ks-intro-sentence { font-size: 26px; }

          /* Base (large-screen) values for every responsive class below -
             declared once, up front, so every @media override that follows
             in source order reliably wins at its breakpoint. A base rule
             declared AFTER a media block would beat it by cascade order
             even when the media condition is true, since @media adds no
             specificity of its own - caught this the hard way with
             .ks-hub-grid staying stuck at 3 columns below 700px. */
          .ks-rail { width: 124px; }
          .ks-rail-logo { width: 36px; height: 36px; }
          .ks-rail-divider { width: 84px; }
          .ks-rail-btn { width: 104px; }
          .ks-draw-widget { width: 104px; }
          .ks-draw-caption-compact { display: none; }
          .ks-hub-grid { grid-template-columns: repeat(3, 1fr); }

          @media (max-width: 1100px) {
            .ks-intro-wrap { padding: 0 48px 36px; }
            .ks-intro-name { font-size: 48px; }
            .ks-intro-sentence { font-size: 23px; }
          }

          /* Narrower breakpoints must come AFTER wider ones here - at a width
             where two of these conditions are both true, the later rule in
             source order wins (media queries carry no specificity of their
             own), so declaration order has to match "widest first". */
          @media (max-width: 980px) {
            .ks-hub-grid { grid-template-columns: repeat(2, 1fr); }
          }

          @media (max-width: 700px) {
            .ks-intro-actions { flex-direction: column; }
            .ks-intro-badges { flex-wrap: wrap; }
            .ks-intro-sentence { max-width: 100% !important; }
            .ks-hub-grid { grid-template-columns: 1fr; }
          }

          @media (max-width: 1100px) {
            .kickstart-panel {
              position: absolute !important;
              left: 124px;
              top: 0;
              bottom: 0;
              z-index: 20;
              box-shadow: 0 18px 44px rgba(0,0,0,.5);
            }
          }

          @media (max-width: 480px) {
            .ks-rail { width: 64px; }
            .ks-rail-logo { width: 32px; height: 32px; }
            .ks-rail-divider { width: 40px; }
            .ks-rail-btn { width: 52px; padding-left: 0; padding-right: 0; }
            .ks-rail-label { display: none; }
            .ks-draw-widget { width: 52px; padding-left: 4px !important; padding-right: 4px !important; }
            .ks-draw-caption { display: none; }
            .ks-draw-caption-compact { display: inline; }
            .kickstart-panel {
              left: 64px !important;
              width: calc(100vw - 64px) !important;
              box-sizing: border-box !important;
            }
            .ks-hub-grid { grid-template-columns: 1fr; }
            .ks-intro-wrap { padding: 0 20px 24px; }
            .ks-intro-name { font-size: 30px; }
            .ks-intro-sentence { font-size: 17px; }
          }

          @media (prefers-reduced-motion: reduce) {
            *, *::before, *::after {
              animation-duration: 0.01ms !important;
              animation-iteration-count: 1 !important;
              transition-duration: 0.01ms !important;
            }
          }
        `
      }} />
      <div data-theme={theme} style={{
        height: '100vh',
        overflow: 'hidden',
        background: COLOR.surfaceBg,
        color: COLOR.textPrimary,
        display: 'flex',
        fontFamily: FONT_UI
      }}>
        <StopwatchTicker />
        <Rail />
        <SlidePanel />
        <MainArea />
      </div>
    </>
  );
}

// -----------------------------
// SIDEBAR
// -----------------------------
// -----------------------------
// VENSTRE STOLPE (alltid synlig, 124px)
// -----------------------------
function Rail() {
  const [state, setState] = useAppState();
  const railPanel = state.ui.railPanel;

  function toggleSection(section) {
    setState({ ...state, ui: { ...state.ui, railPanel: railPanel === section ? null : section } });
  }

  const showBottomTime = state.ui.view === "activity" && !railPanel && state.stopwatch.running;

  function surpriseMe() {
    const others = ACTIVITIES.filter(a => !a.external);
    const a = others[randInt(0, others.length - 1)];
    setState({
      ...state,
      ui: { ...state.ui, view: "activity", activeActivity: a.id, railPanel: null, activityKey: state.ui.activityKey + 1 },
      draw: { ...state.draw, pool: [], current: null, total: 0 }
    });
    if (window.gtag) window.gtag('event', 'tilfeldig_aktivitet_klikk', { aktivitet_navn: a.id });
  }

  return (
    <div className="ks-rail" style={{
      flex: 'none',
      background: COLOR.railBg,
      borderRight: '1px solid rgba(255,255,255,.07)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '16px 0',
      gap: '10px',
      overflowY: 'auto'
    }}>
      <div
        className="ks-rail-logo"
        style={{
          borderRadius: '10px',
          background: COLOR.accent, color: COLOR.accentOnDark,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '19px',
          cursor: 'pointer', flex: 'none'
        }}
        onClick={() => setState({ ...state, ui: { ...state.ui, view: "hub", activeActivity: null, railPanel: null } })}
        title="Til startsiden"
      >
        K
      </div>

      <DrawField />

      <div className="ks-rail-divider" style={{ height: '1px', background: 'rgba(255,255,255,.07)', margin: '4px 0' }} />

      <RailToolButton icon="🏠" label="Aktiviteter" active={state.ui.view === "hub"} onClick={() => setState({ ...state, ui: { ...state.ui, view: "hub", activeActivity: null, railPanel: null } })} />
      <div className="ks-rail-divider" style={{ height: '1px', background: 'rgba(255,255,255,.07)', margin: '4px 0' }} />
      <RailToolButton icon="👥" label="Klasse" active={railPanel === 'klasse'} onClick={() => toggleSection('klasse')} />
      <RailToolButton icon="⏱️" label="Klokke" active={railPanel === 'timer'} dot={state.stopwatch.running} onClick={() => toggleSection('timer')} />
      <RailToolButton icon="🏆" label="Poeng" active={railPanel === 'poeng'} dot={state.scores.enabled} onClick={() => toggleSection('poeng')} />
      <FullscreenRailButton />

      <div style={{ flex: 1 }} />

      <div className="ks-rail-divider" style={{ height: '1px', background: 'rgba(255,255,255,.1)', margin: '4px 0' }} />
      <RailToolButton icon="🎲" label="Overrask meg" active={false} onClick={surpriseMe} />

      {showBottomTime && <RailStatusChip label="TID" value={formatClock(state.stopwatch.remaining)} />}
    </div>
  );
}

function RailToolButton({ icon, label, active, dot, onClick }) {
  return (
    <button
      className="ks-rail-btn"
      onClick={onClick}
      title={label}
      style={{
        padding: '10px 0 8px',
        borderRadius: '12px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '4px',
        background: active ? COLOR.activeIconBg : 'transparent',
        border: 'none',
        cursor: 'pointer',
        position: 'relative'
      }}
      onMouseOver={(e) => { if (!active) e.currentTarget.style.background = COLOR.activeIconBg; }}
      onMouseOut={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
    >
      <span style={{ fontSize: '21px', position: 'relative', color: COLOR.chromeText }}>
        {icon}
        {dot && (
          <span style={{
            position: 'absolute', top: '-2px', right: '-7px',
            width: '7px', height: '7px', borderRadius: '99px', background: COLOR.accent
          }} />
        )}
      </span>
      <span className="ks-rail-label" style={{ fontSize: '13px', fontFamily: FONT_UI, color: COLOR.railLabel }}>{label}</span>
    </button>
  );
}

function FullscreenRailButton() {
  const [isFs, setIsFs] = useState(!!document.fullscreenElement);
  useEffect(() => {
    function onChange() { setIsFs(!!document.fullscreenElement); }
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);
  const toggle = async () => {
    if (!document.fullscreenElement) await document.documentElement.requestFullscreen();
    else await document.exitFullscreen();
  };
  return <RailToolButton icon="⛶" label="Fullskjerm" active={isFs} onClick={toggle} />;
}

function RailStatusChip({ label, value }) {
  return (
    <div className="ks-rail-btn" style={{
      background: COLOR.chromeSurface, borderRadius: '12px',
      padding: '8px 6px', textAlign: 'center'
    }}>
      <div style={{ fontSize: '11px', color: COLOR.textSecondaryOnPanel, letterSpacing: '.05em', marginBottom: '2px' }}>{label}</div>
      <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '18px', color: COLOR.accent }}>{value}</div>
    </div>
  );
}

function formatClock(totalSeconds) {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

// -----------------------------
// ELEVTREKK-FELT (mest brukte funksjon)
// -----------------------------
// Delt trekk-elev-tilstand: samme kilde (state.draw + aktiv klasse/lag) brukt av
// stolpens DrawField og av "Trekk elev" på introskjermene. Én implementasjon av
// trekk-logikken, ikke elleve.
function useDrawStudent() {
  const [state, setState] = useAppState();
  const [justRestarted, setJustRestarted] = useState(false);
  const active = state.classes.find((c) => c.id === state.activeClassId) || state.classes[0];
  const draw = state.draw;
  const source = draw.mode === "team" ? (active?.teams || []).map(t => t.name) : (active?.students || []);

  function drawNext() {
    if (!source.length) return;
    const wasExhausted = draw.current && draw.pool.length === 0 && draw.total > 0;
    const remaining = draw.pool.length ? draw.pool : source;
    const pick = remaining[randInt(0, remaining.length - 1)];
    const idx = remaining.indexOf(pick);
    const poolAfter = [...remaining.slice(0, idx), ...remaining.slice(idx + 1)];
    setState({
      ...state,
      draw: { ...draw, pool: poolAfter, current: pick, total: draw.pool.length ? draw.total : source.length }
    });
    beep(150, 1400);
    if (wasExhausted) {
      setJustRestarted(true);
      setTimeout(() => setJustRestarted(false), 4000);
    }
  }

  function resetDraw() {
    setState({ ...state, draw: { ...draw, pool: [], current: null, total: 0 } });
    setJustRestarted(false);
  }

  const isEmpty = draw.current && draw.pool.length === 0 && draw.total > 0;
  return { current: draw.current, canDraw: source.length > 0, poolRemaining: draw.pool.length, isEmpty, justRestarted, drawNext, resetDraw };
}

// Bryter (segmentert velger) for forvalg som Emoji-historie (3/5 emojier) og
// Tidslinje (3/4/5 hendelser) — samme komponent på introskjermen og inne i aktiviteten,
// så læreren kan skifte midt i runden uten å gå ut (design_handoff_introskjerm/README.md).
function Bryter({ value, onChange, options, label, small = false }) {
  return (
    <div role="radiogroup" aria-label={label} style={{
      display: 'flex', gap: '4px', padding: small ? '4px' : '5px', background: COLOR.chromeSurface,
      border: `1px solid ${COLOR.shellBorder}`, borderRadius: small ? '11px' : '12px'
    }}>
      {options.map(opt => (
        <button
          key={opt.value} role="radio" aria-checked={value === opt.value}
          onClick={() => onChange(opt.value)}
          style={{
            border: 'none', borderRadius: '8px', fontFamily: FONT_UI, fontWeight: 600, cursor: 'pointer',
            padding: small ? '7px 15px' : '8px 20px', fontSize: small ? '14px' : '17px',
            background: value === opt.value ? COLOR.accent : 'transparent',
            color: value === opt.value ? COLOR.accentOnDark : COLOR.textSecondary
          }}
        >{opt.label}</button>
      ))}
    </div>
  );
}

// Anatomien alle introskjermer følger (design_handoff_introskjerm/README.md):
// ikon+navn, én setning, korte merker (bare når runden krever noe), handlingslinje.
// `badges`: [{ icon, label, required }]. `toggle`: { value, onChange, options:[{label,value}], helperText }.
function IntroScreen({ icon, name, sentence, badges = [], toggle, primaryLabel, onPrimary, showDraw = false }) {
  const drawApi = useDrawStudent();
  return (
    <div className="ks-intro-wrap" style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      gap: '28px', width: '100%', maxWidth: '900px'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <span aria-hidden="true" style={{ fontSize: '46px', lineHeight: 1 }}>{icon}</span>
        <h1 className="ks-intro-name" style={{
          fontFamily: FONT_DISPLAY, fontWeight: 800, letterSpacing: '-.025em', color: COLOR.textPrimary, margin: 0
        }}>{name}</h1>
      </div>

      <p className="ks-intro-sentence" style={{
        lineHeight: 1.35, color: COLOR.textCard, textAlign: 'center', maxWidth: '720px', textWrap: 'pretty', margin: 0
      }}>{sentence}</p>

      {badges.length > 0 && (
        <div className="ks-intro-badges" style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
          {badges.map((b, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: '6px', borderRadius: '99px', padding: '8px 16px', fontSize: '16px',
              background: b.required ? COLOR.badgeReqBg : COLOR.badgeBg,
              border: `1px solid ${b.required ? COLOR.badgeReqBorder : COLOR.badgeBorder}`,
              color: b.required ? COLOR.badgeReqText : COLOR.badgeText
            }}>
              {b.icon && <span aria-hidden="true">{b.icon}</span>}
              <span>{b.label}</span>
            </div>
          ))}
        </div>
      )}

      {toggle && (
        <Bryter value={toggle.value} onChange={toggle.onChange} options={toggle.options} label={toggle.label} />
      )}

      <div className="ks-intro-actions" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
        <button onClick={onPrimary} style={introPrimaryBtnStyle()}>{primaryLabel}</button>
        {showDraw && (
          <button
            onClick={drawApi.drawNext} disabled={!drawApi.canDraw}
            style={introSecondaryBtnStyle({ opacity: drawApi.canDraw ? 1 : .5, cursor: drawApi.canDraw ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', gap: '8px' })}
            onMouseOver={(e) => { if (drawApi.canDraw) e.currentTarget.style.borderColor = COLOR.accent; }}
            onMouseOut={(e) => { e.currentTarget.style.borderColor = COLOR.btnSecondaryBorder; }}
          ><span aria-hidden="true">🎯</span> Trekk elev</button>
        )}
      </div>

      {toggle?.helperText && (
        <div style={{ fontSize: '15px', color: COLOR.textSecondary, textAlign: 'center', marginTop: '-14px' }}>{toggle.helperText}</div>
      )}

      {showDraw && drawApi.current && (
        <button
          onClick={drawApi.resetDraw} title="Klikk for å fjerne"
          style={{
            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
            fontSize: '16px', color: COLOR.textSecondary, marginTop: '-14px', fontFamily: FONT_UI
          }}
          onMouseOver={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
          onMouseOut={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
        ><span aria-live="polite">{drawApi.current}</span></button>
      )}
    </div>
  );
}

function DrawField() {
  const drawApi = useDrawStudent();
  const { current, canDraw, poolRemaining, isEmpty, justRestarted, drawNext, resetDraw } = drawApi;

  if (!current) {
    return (
      <button
        className="ks-draw-widget"
        onClick={drawNext}
        disabled={!canDraw}
        title="Trekk elev"
        style={{
          background: COLOR.chromeSurface, borderRadius: '14px',
          border: '1px dashed rgba(255,255,255,.22)', padding: '16px 8px',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
          cursor: canDraw ? 'pointer' : 'not-allowed', opacity: canDraw ? 1 : 0.5
        }}
        onMouseOver={(e) => e.currentTarget.style.borderColor = COLOR.accent}
        onMouseOut={(e) => e.currentTarget.style.borderColor = 'rgba(255,255,255,.22)'}
      >
        <span style={{ fontSize: '26px' }}>🎯</span>
        <span className="ks-draw-caption" style={{ fontSize: '15px', fontWeight: 600, color: COLOR.chromeText, textAlign: 'center' }}>Trekk elev</span>
      </button>
    );
  }

  return (
    <div className="ks-draw-widget" style={{
      background: COLOR.chromeSurface, borderRadius: '14px',
      border: '1px solid rgba(255,176,32,.4)', padding: '13px 8px 11px',
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '7px'
    }}>
      <span className="ks-draw-caption" style={{ fontSize: '13px', color: COLOR.textSecondaryOnPanel, letterSpacing: '.05em' }}>TRUKKET</span>
      <button
        onClick={resetDraw} title="Klikk for å fjerne"
        style={{
          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '24px', color: COLOR.accent,
          lineHeight: 1.1, textAlign: 'center', wordBreak: 'break-word'
        }}
      >{current}</button>
      <button
        onClick={drawNext} title="Trekk ny"
        style={{
          width: '100%', padding: '8px 0', borderRadius: '9px', border: 'none',
          background: COLOR.accent, color: COLOR.accentOnDark, fontFamily: FONT_UI,
          fontWeight: 600, fontSize: '14px', cursor: 'pointer'
        }}
      >
        <span className="ks-draw-caption">Trekk ny</span><span className="ks-draw-caption-compact">↻</span>
      </button>
      <span className="ks-draw-caption" style={{ fontSize: '13px', color: COLOR.textSecondaryOnPanel, textAlign: 'center' }}>
        {justRestarted ? "Alle har vært trukket – ny runde!" : isEmpty ? "Alle trukket" : `${poolRemaining} igjen`}
      </span>
      <button onClick={resetDraw} title="Nullstill" className="ks-draw-caption" style={{
        background: 'none', border: 'none', color: COLOR.textSecondaryOnPanel, textDecoration: 'underline',
        padding: 0, fontFamily: FONT_UI, fontSize: '12px', cursor: 'pointer'
      }}>Nullstill</button>
    </div>
  );
}

// --- Aktivitet: 5 på 30 sekunder
const FIVE_IN_30_CATEGORIES = [
  "Land i Europa", "YouTubere", "Ting man kan gjøre på ferie", "Norske byer",
  "Dyr som lever i havet", "Ting i et klasserom", "Matvarer som er røde", "Ting som kunne skjedd på en helt vanlig mandag", "Yrker", "Bilmerker", "Sosiale medier", "Videospill", "Filmer", "Matvarer du aldri ville hatt på pizza", 
  "Netflix-serier", "Ting man spiser til frokost", "Instrumenter", "Land i Afrika", "Norske politikere", 
  "Land i Asia", "Fotballklubber", "Ting som er grønne", "Kjente personer", "Dyr du helst ikke vil møte i en skog", 
  "Ting man gjør på skolen", "Dyr i Afrika", "Ting i kjøleskapet", "Nynorske verb", "Ting som alltid blir ødelagt på skolen", "Ting lærere sier ofte", "Ting som kan få deg til å le", "Forskjellige fugler", "Superkrefter", 
  "Mobil-apper", "Språk", "Norske artister", "Planeter", "Ting som flyr", "Ting du ville hatt med på en øde øy", 
  "Klær", "Ting man gjør på sommeren", "Skolefag", "Ting som er kalde", "Måter å si «hei» på", 
  "Ting man kan samle på", "Land i Amerika", "TV-kanaler", "Ting på et bibliotek"
];

function FiveIn30() {
  const [started, setStarted] = useState(false);
  const [category, setCategory] = useState("");
  const [usedCategories, setUsedCategories] = useState([]);
  const timer = useCountdown(30);

  function pickCategory() {
    const availableCategories = usedCategories.length >= FIVE_IN_30_CATEGORIES.length
      ? FIVE_IN_30_CATEGORIES
      : FIVE_IN_30_CATEGORIES.filter(cat => !usedCategories.includes(cat));
    const newCategory = availableCategories[randInt(0, availableCategories.length - 1)];
    setCategory(newCategory);
    setUsedCategories(usedCategories.length >= FIVE_IN_30_CATEGORIES.length ? [newCategory] : [...usedCategories, newCategory]);
  }

  function start() {
    pickCategory();
    setStarted(true);
  }

  function newRound() {
    pickCategory();
    timer.reset();
  }

  if (!started) {
    return (
      <IntroScreen
        icon="⏱️" name="5 på 30 sekunder"
        sentence="Ramse opp 5 ting innen kategorien."
        badges={[{ icon: '⏱', label: '30 sekunder' }]}
        primaryLabel="Vis kategori" onPrimary={start} showDraw
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '36px', width: '100%', maxWidth: '900px' }}>
      <div aria-live="polite" style={{
        fontSize: 'clamp(40px, 5vw, 76px)', fontWeight: 800, fontFamily: FONT_DISPLAY,
        textAlign: 'center', lineHeight: 1.06, letterSpacing: '-.02em', color: COLOR.textPrimary
      }}>5 {category}</div>
      <BareTimer remaining={timer.remaining} total={30} />
      <TimerActionBar
        running={timer.running} remaining={timer.remaining}
        onStart={timer.start} onStop={timer.stop} onRestart={timer.restart}
        onNext={newRound} nextLabel="Ny kategori"
      />
    </div>
  );
}

// --- Aktivitet: Emoji-ansikt
const EMOJI_FACES = [
  "😀", "😁", "😂", "🤣", "😊", "😇", "🙂", "🙃", "😉", "😍", "🥰", "😘", 
  "😗", "☺️", "😚", "😙", "😋", "😛", "😝", "😜", "🤪", "🤨", "🧐", "🤓", 
  "😎", "🤩", "🥳", "😏", "😒", "😞", "😔", "😟", "😕", "🙁", "☹️", "😣", 
  "😖", "😫", "😩", "🥺", "😢", "😭", "😤", "😠", "😡", "🤬", "🤯", "😳", 
  "🥵", "🥶", "😱", "😨", "😰", "😥", "😓", "🤗", "🤔", "🤭", "🤫", "🤥", 
  "😶", "😐", "😑", "😬", "🙄", "😯", "😦", "😧", "😮", "😲", "🥱", "😴", 
  "🤤", "😪", "😵", "🤐", "🥴", "🤢", "🤮", "🤧", "😷", "🤒", "🤕"
];

function EmojiFace() {
  const [started, setStarted] = useState(false);
  const [emoji, setEmoji] = useState("");
  const [usedEmojis, setUsedEmojis] = useState([]);
  const [showingDuration, setShowingDuration] = useState(false);

  function pickEmoji() {
    const availableEmojis = usedEmojis.length >= EMOJI_FACES.length
      ? EMOJI_FACES
      : EMOJI_FACES.filter(em => !usedEmojis.includes(em));
    const newEmoji = availableEmojis[randInt(0, availableEmojis.length - 1)];
    setEmoji(newEmoji);
    setShowingDuration(true);
    setTimeout(() => setShowingDuration(false), 5000);
    setUsedEmojis(usedEmojis.length >= EMOJI_FACES.length ? [newEmoji] : [...usedEmojis, newEmoji]);
  }

  function start() {
    pickEmoji();
    setStarted(true);
  }

  if (!started) {
    return (
      <IntroScreen
        icon="😄" name="Emoji-ansikt"
        sentence="Vis ansiktet som emojien viser med kroppen."
        badges={[{ icon: '⏱', label: '5 sekunder' }]}
        primaryLabel="Vis emoji" onPrimary={start} showDraw
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '36px', width: '100%', maxWidth: '900px' }}>
      <div aria-live="polite" style={{ fontSize: '160px', lineHeight: 1 }}>{emoji}</div>
      <div style={{ fontSize: '19px', color: showingDuration ? COLOR.accentText : COLOR.textSecondary, fontWeight: showingDuration ? 700 : 400 }}>
        {showingDuration ? "Vis dette ansiktet nå!" : "Tiden er ute! Hvem viste ansiktet best?"}
      </div>
      <button onClick={pickEmoji} style={mainPrimaryBtnStyle()}>Nytt ansikt</button>
    </div>
  );
}

function SlidePanel() {
  const [state] = useAppState();
  const section = state.ui.railPanel;
  if (!section) return null;

  return (
    <div className="kickstart-panel" style={{
      width: '292px', flex: 'none', background: COLOR.panelBg,
      borderRight: '1px solid rgba(255,255,255,.07)',
      padding: '22px 20px', display: 'flex', flexDirection: 'column', gap: '20px',
      overflowY: 'auto'
    }}>
      <TrukketElevSection />
      <div style={{ height: '1px', background: 'rgba(255,255,255,.07)' }} />
      {section === 'klasse' && <KlassePanelSection />}
      {section === 'timer' && <KlokkePanelSection />}
      {section === 'poeng' && <PoengPanelSection />}
    </div>
  );
}

// -----------------------------
// HOVEDFLATE (hub eller aktiv aktivitet)
// -----------------------------
function MainArea() {
  const [state] = useAppState();
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, background: COLOR.surfaceBg }}>
      {state.ui.view === "hub" ? <HubView /> : <ActivityView />}
    </div>
  );
}

function KlassePanelSection() {
  const [state, setState] = useAppState();
  const { classes, activeClassId } = state;
  const active = classes.find((c) => c.id === activeClassId) || classes[0];
  const [newClassName, setNewClassName] = useState("");
  const [studentName, setStudentName] = useState("");
  const [studentError, setStudentError] = useState("");
  const [showTeams, setShowTeams] = useState(() => (active?.teams?.length || 0) > 0);

  function setActive(id) { setState({ ...state, activeClassId: id }); }

  function addClass() {
    if (!newClassName.trim()) return;
    const id = `class-${Date.now()}`;
    setState({ ...state, classes: [...classes, { id, name: newClassName.trim(), students: [], teams: [] }], activeClassId: id });
    setNewClassName("");
  }

  function deleteClass(classId) {
    if (classes.length <= 1) { alert("Du må ha minst én klasse!"); return; }
    const classToDelete = classes.find(c => c.id === classId);
    if (!window.confirm(`Vil du slette klassen "${classToDelete?.name}"?`)) return;
    const newClasses = classes.filter(c => c.id !== classId);
    const newActiveId = classId === activeClassId ? newClasses[0].id : activeClassId;
    setState({ ...state, classes: newClasses, activeClassId: newActiveId });
  }

  function addStudent() {
    if (!studentName.trim()) return;
    const s = studentName.trim();
    if (active.students.includes(s)) { setStudentError(`«${s}» er allerede i klassen.`); return; }
    updateClass(active.id, { students: [...active.students, s] });
    setStudentName("");
    setStudentError("");
  }

  function removeStudent(s) {
    const clearsDraw = state.draw.current === s || state.draw.pool.includes(s);
    setState({
      ...state,
      classes: classes.map((c) => (c.id === active.id ? { ...c, students: c.students.filter((x) => x !== s) } : c)),
      draw: clearsDraw ? { ...state.draw, pool: [], current: null, total: 0 } : state.draw
    });
  }
  function updateClass(id, patch) { setState({ ...state, classes: classes.map((c) => (c.id === id ? { ...c, ...patch } : c)) }); }

  function createTeams() {
    if (!active?.students?.length) return;
    const numTeams = parseInt(prompt("Hvor mange lag? (2-6)", "2")) || 2;
    if (numTeams < 2 || numTeams > 6) return;
    const shuffled = shuffle([...active.students]);
    const teams = Array.from({ length: numTeams }, (_, i) => ({ id: `team-${i + 1}`, name: `Lag ${i + 1}`, members: [] }));
    shuffled.forEach((student, index) => { teams[index % numTeams].members.push(student); });
    updateClass(active.id, { teams });
    setShowTeams(true);
  }

  function clearTeams() {
    const newDraw = state.draw.mode === "team" ? { ...state.draw, mode: "student", pool: [], current: null, total: 0 } : state.draw;
    setState({ ...state, classes: classes.map((c) => (c.id === active.id ? { ...c, teams: [] } : c)), draw: newDraw });
    setShowTeams(false);
  }
  function deleteTeam(teamId) {
    const remaining = (active.teams || []).filter((t) => t.id !== teamId);
    const newDraw = (!remaining.length && state.draw.mode === "team")
      ? { ...state.draw, mode: "student", pool: [], current: null, total: 0 }
      : state.draw;
    setState({ ...state, classes: classes.map((c) => (c.id === active.id ? { ...c, teams: remaining } : c)), draw: newDraw });
    if (!remaining.length) setShowTeams(false);
  }
  function clearAllStudents() {
    if (!window.confirm(`Vil du slette alle elever fra ${active?.name}?`)) return;
    setState({
      ...state,
      classes: classes.map((c) => (c.id === active.id ? { ...c, students: [], teams: [] } : c)),
      draw: { ...state.draw, pool: [], current: null, total: 0 }
    });
    setShowTeams(false);
  }

  function exportClass() {
    if (!active) return;
    const dataStr = JSON.stringify({ name: active.name, students: active.students, teams: active.teams, exportDate: new Date().toISOString() }, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${active.name.replace(/[^a-zA-Z0-9]/g, '_')}_elevliste.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  function importClass() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const imported = JSON.parse(e.target.result);
          if (!imported.name || !Array.isArray(imported.students)) { alert('Ugyldig filformat!'); return; }
          const newId = `imported-${Date.now()}`;
          const newClass = { id: newId, name: imported.name, students: imported.students || [], teams: imported.teams || [] };
          setState({ ...state, classes: [...classes, newClass], activeClassId: newId });
          alert(`Importerte klasse "${imported.name}" med ${imported.students.length} elever!`);
        } catch (error) {
          alert('Kunne ikke lese filen. Sjekk at det er en gyldig JSON-fil.');
        }
      };
      reader.readAsText(file);
    };
    input.click();
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div style={panelLabelStyle()}>KLASSE</div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {classes.map((c) => (
          <button key={c.id} onClick={() => setActive(c.id)} style={{
            padding: '6px 12px', borderRadius: '9px', border: 'none', cursor: 'pointer',
            fontFamily: FONT_UI, fontSize: '13px', fontWeight: 600,
            background: c.id === active?.id ? COLOR.accent : COLOR.chromeSecondary,
            color: c.id === active?.id ? COLOR.accentOnDark : COLOR.chromeText
          }}>{c.name}</button>
        ))}
        {classes.length > 1 && (
          <button onClick={() => deleteClass(active?.id)} title="Slett klasse" style={{
            ...miniBtnStyle('minus'), width: 'auto', padding: '0 8px', borderRadius: '9px'
          }}>🗑️</button>
        )}
      </div>

      <div style={{ display: 'flex', gap: '6px' }}>
        <input
          style={panelInputStyle({ flex: 1 })}
          placeholder="Ny klasse…"
          value={newClassName}
          onChange={(e) => setNewClassName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addClass()}
        />
        <button onClick={addClass} style={miniBtnStyle('plus')}>+</button>
      </div>

      <div style={{ height: '1px', background: 'rgba(255,255,255,.07)' }} />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', maxHeight: '160px', overflowY: 'auto' }}>
        {!showTeams ? (
          active?.students?.map((s) => (
            <span key={s} style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              background: COLOR.track, border: '1px solid rgba(255,255,255,.07)',
              borderRadius: '999px', padding: '5px 8px 5px 12px', fontSize: '13px', color: COLOR.chipText
            }}>
              {s}
              <button onClick={() => removeStudent(s)} style={{
                background: 'none', border: 'none', color: COLOR.textSecondary, cursor: 'pointer', fontSize: '12px', padding: 0
              }}>✕</button>
            </span>
          ))
        ) : (
          active?.teams?.map((team) => (
            <div key={team.id} style={{
              width: '100%', background: COLOR.cardAlt, borderRadius: '10px', padding: '10px 12px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                <div style={{ fontWeight: 700, fontSize: '13px', color: COLOR.accent }}>{team.name}</div>
                <button onClick={() => deleteTeam(team.id)} title="Slett lag" style={{
                  background: 'none', border: 'none', color: COLOR.textSecondary, cursor: 'pointer', fontSize: '12px', padding: 0
                }}>✕</button>
              </div>
              <div style={{ fontSize: '12px', color: COLOR.textSecondary }}>{team.members.join(', ')}</div>
            </div>
          ))
        )}
      </div>

      <div style={{ display: 'flex', gap: '6px' }}>
        <input
          style={panelInputStyle({ flex: 1 })}
          placeholder="Legg til elev…"
          value={studentName}
          onChange={(e) => { setStudentName(e.target.value); if (studentError) setStudentError(""); }}
          onKeyDown={(e) => e.key === "Enter" && addStudent()}
        />
        <button onClick={addStudent} style={miniBtnStyle('plus')}>+</button>
      </div>
      {studentError && (
        <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '-2px' }}>{studentError}</div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        <button onClick={() => setShowTeams(!showTeams)} style={quietBtnStyle({ fontSize: '13px', padding: '6px 10px' })}>
          {showTeams ? "Vis elever" : "Vis lag"}
        </button>
        <button onClick={createTeams} style={quietBtnStyle({ fontSize: '13px', padding: '6px 10px' })}>
          Lag tilfeldige lag
        </button>
        {active?.teams?.length > 0 && (
          <button onClick={clearTeams} style={quietBtnStyle({ fontSize: '13px', padding: '6px 10px' })}>Fjern lag</button>
        )}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        <button onClick={exportClass} style={quietBtnStyle({ fontSize: '13px', padding: '6px 10px' })}>Eksporter</button>
        <button onClick={importClass} style={quietBtnStyle({ fontSize: '13px', padding: '6px 10px' })}>Importer</button>
        {active?.students?.length > 0 && (
          <button onClick={clearAllStudents} style={quietBtnStyle({ fontSize: '13px', padding: '6px 10px', color: '#ef4444', borderColor: 'rgba(239,68,68,.4)' })}>
            Slett alle elever
          </button>
        )}
      </div>
    </div>
  );
}

function TrukketElevSection() {
  const [state, setState] = useAppState();
  const active = state.classes.find((c) => c.id === state.activeClassId) || state.classes[0];
  const draw = state.draw;
  const [justRestarted, setJustRestarted] = useState(false);

  function sourceList(mode) {
    return mode === "team" ? (active?.teams || []).map(t => t.name) : (active?.students || []);
  }

  function drawNow() {
    const source = sourceList(draw.mode);
    if (!source.length) return;
    const wasExhausted = draw.current && draw.pool.length === 0 && draw.total > 0;
    const remaining = draw.pool.length ? draw.pool : source;
    const n = Math.min(draw.count, remaining.length);
    const picks = shuffle(remaining).slice(0, n);
    const poolAfter = remaining.filter(x => !picks.includes(x));
    setState({ ...state, draw: { ...draw, pool: poolAfter, current: picks.join(", "), total: draw.pool.length ? draw.total : source.length } });
    beep(150, 1400);
    if (wasExhausted) {
      setJustRestarted(true);
      setTimeout(() => setJustRestarted(false), 4000);
    }
  }

  function resetDraw() { setState({ ...state, draw: { ...draw, pool: [], current: null, total: 0 } }); }
  function setMode(mode) { setState({ ...state, draw: { ...draw, mode, pool: [], current: null, total: 0 } }); }
  function setCount(n) { setState({ ...state, draw: { ...draw, count: Math.max(1, Math.min(5, n)) } }); }

  const source = sourceList(draw.mode);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div style={panelLabelStyle()}>TRUKKET ELEV</div>

      <div style={{
        background: COLOR.chromeSurface, border: '1px solid rgba(255,176,32,.35)', borderRadius: '14px',
        padding: '20px 14px', textAlign: 'center'
      }}>
        {draw.current ? (
          <button
            onClick={resetDraw} title="Klikk for å fjerne"
            style={{
              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '30px', color: COLOR.accent
            }}
          >{draw.current}</button>
        ) : (
          <span style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '30px', color: COLOR.accent }}>—</span>
        )}
      </div>

      <div style={{ display: 'flex', gap: '6px', fontSize: '12px' }}>
        <button onClick={() => setMode("student")} style={{
          ...quietBtnStyle({ flex: 1, fontSize: '12px', padding: '6px 0', textAlign: 'center', justifyContent: 'center' }),
          background: draw.mode === "student" ? COLOR.accent : 'transparent',
          color: draw.mode === "student" ? COLOR.accentOnDark : COLOR.textSecondary,
          border: draw.mode === "student" ? 'none' : '1px solid rgba(255,255,255,.14)'
        }}>Elever</button>
        <button onClick={() => setMode("team")} disabled={!active?.teams?.length} style={{
          ...quietBtnStyle({ flex: 1, fontSize: '12px', padding: '6px 0', textAlign: 'center', justifyContent: 'center' }),
          background: draw.mode === "team" ? COLOR.accent : 'transparent',
          color: draw.mode === "team" ? COLOR.accentOnDark : COLOR.textSecondary,
          border: draw.mode === "team" ? 'none' : '1px solid rgba(255,255,255,.14)',
          opacity: active?.teams?.length ? 1 : 0.5
        }}>Lag</button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ fontSize: '12px', color: COLOR.textSecondary }}>Antall:</span>
        <input type="number" min={1} max={5} value={draw.count}
          onChange={(e) => setCount(parseInt(e.target.value || 1))}
          style={panelInputStyle({ width: '44px', padding: '5px 6px', fontSize: '12px' })} />
      </div>

      <button onClick={drawNow} disabled={!source.length} style={secondaryBtnStyle({
        width: '100%', textAlign: 'center', justifyContent: 'center', cursor: source.length ? 'pointer' : 'not-allowed', opacity: source.length ? 1 : 0.5
      })}>Trekk ny elev</button>

      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: COLOR.textSecondaryOnPanel }}>
        <span>{justRestarted ? "Alle har vært trukket – ny runde!" : `${draw.pool.length} av ${draw.total} igjen i bunken`}</span>
        <button onClick={() => { resetDraw(); setJustRestarted(false); }} style={linkBtnStyle()}>Nullstill</button>
      </div>
    </div>
  );
}

function useStopwatchActions() {
  const [state, setState] = useAppState();
  const { remaining, duration } = state.stopwatch;
  function start() {
    setState({ ...state, stopwatch: { ...state.stopwatch, running: true, remaining: remaining > 0 ? remaining : duration } });
  }
  function pause() { setState({ ...state, stopwatch: { ...state.stopwatch, running: false } }); }
  function reset() { setState({ ...state, stopwatch: { ...state.stopwatch, running: false, remaining: duration } }); }
  function setDuration(sec) { setState({ ...state, stopwatch: { ...state.stopwatch, duration: sec, remaining: sec, running: false } }); }
  return { running: state.stopwatch.running, remaining, duration, start, pause, reset, setDuration };
}

function StopwatchTicker() {
  const [state, setState] = useAppState();
  const { running, remaining } = state.stopwatch;
  const stateRef = useRef(state);
  stateRef.current = state;
  const endAtRef = useRef(null);

  useEffect(() => {
    let t;
    if (running) {
      endAtRef.current = Date.now() + remaining * 1000;
      t = setInterval(() => {
        const ms = endAtRef.current - Date.now();
        const cur = stateRef.current;
        if (ms <= 0) {
          clearInterval(t);
          beep(600, 1000);
          setState({ ...cur, stopwatch: { ...cur.stopwatch, running: false, remaining: 0 } });
        } else {
          setState({ ...cur, stopwatch: { ...cur.stopwatch, remaining: Math.ceil(ms / 1000) } });
        }
      }, 200);
    }
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, remaining]);

  return null;
}

function KlokkePanelSection() {
  const { running, remaining, duration, start, pause, reset, setDuration } = useStopwatchActions();
  const frac = duration > 0 ? Math.max(0, remaining / duration) : 0;
  const circumference = 276;
  const offset = circumference * (1 - frac);
  const [customMin, setCustomMin] = useState('');
  const [customSec, setCustomSec] = useState('');

  function applyCustom() {
    const m = parseInt(customMin, 10) || 0;
    const s = parseInt(customSec, 10) || 0;
    const total = m * 60 + s;
    if (total > 0) setDuration(Math.min(total, 5999));
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div style={panelLabelStyle()}>KLOKKE</div>
      <div style={{ position: 'relative', width: '172px', height: '172px', margin: '0 auto' }}>
        <svg width="172" height="172" viewBox="0 0 100 100" style={{ transform: 'rotate(-90deg)' }}>
          <circle cx="50" cy="50" r="44" fill="none" stroke={COLOR.track} strokeWidth="8" />
          <circle cx="50" cy="50" r="44" fill="none" stroke={COLOR.accent} strokeWidth="8"
            strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset} />
        </svg>
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center'
        }}>
          <span style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '42px' }}>{formatClock(remaining)}</span>
          <span style={{ fontSize: '12px', color: COLOR.textSecondary }}>av {formatClock(duration)}</span>
        </div>
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button onClick={running ? pause : start} style={primaryBtnStyle({ flex: 1, textAlign: 'center' })}>
          {running ? "Pause" : "Start"}
        </button>
        <button onClick={reset} style={secondaryBtnStyle({ flex: 1, textAlign: 'center' })}>Nullstill</button>
      </div>
      <div style={{ display: 'flex', gap: '6px' }}>
        {[30, 60, 120, 300].map((s) => (
          <button key={s} onClick={() => setDuration(s)} style={{
            flex: 1, background: COLOR.cardAlt, border: '1px solid rgba(255,255,255,.07)',
            borderRadius: '9px', padding: '8px 0', fontSize: '13px', color: '#99A2B0', cursor: 'pointer', fontFamily: FONT_UI
          }}>{s < 60 ? `${s}s` : `${s / 60}min`}</button>
        ))}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <div style={panelLabelStyle()}>EGEN TID</div>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <input type="number" min={0} max={99} placeholder="min" value={customMin}
            onChange={(e) => setCustomMin(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && applyCustom()}
            style={panelInputStyle({ width: '56px', textAlign: 'center' })} />
          <span style={{ color: COLOR.textSecondary }}>:</span>
          <input type="number" min={0} max={59} placeholder="sek" value={customSec}
            onChange={(e) => setCustomSec(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && applyCustom()}
            style={panelInputStyle({ width: '56px', textAlign: 'center' })} />
          <button onClick={applyCustom} style={secondaryBtnStyle({ flex: 1, textAlign: 'center', padding: '8px 0', fontSize: '13px' })}>Sett</button>
        </div>
      </div>
    </div>
  );
}

function FloatingStopwatch() {
  const { running, remaining, pause, reset } = useStopwatchActions();
  if (!running) return null;
  return (
    <div style={{
      position: 'absolute', left: '22px', bottom: '22px', width: '224px',
      background: COLOR.popover, border: '1px solid rgba(255,255,255,.12)', borderRadius: '14px',
      boxShadow: '0 18px 44px rgba(0,0,0,.5)', padding: '14px',
      display: 'flex', flexDirection: 'column', gap: '11px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={panelLabelStyle()}>STOPPEKLOKKE</span>
        <button onClick={pause} style={{ background: 'none', border: 'none', color: COLOR.textSecondary, cursor: 'pointer', fontSize: '14px' }}>✕</button>
      </div>
      <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '40px' }}>{formatClock(remaining)}</div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button onClick={pause} style={primaryBtnStyle({ flex: 1, textAlign: 'center', padding: '9px 0' })}>Pause</button>
        <button onClick={reset} style={secondaryBtnStyle({ flex: 1, textAlign: 'center', padding: '9px 0' })}>Nullstill</button>
      </div>
    </div>
  );
}

function PoengPanelSection() {
  const [state, setState] = useAppState();
  const { enabled, teams } = state.scores;
  const [name, setName] = useState("");

  function toggleEnabled(v) { setState({ ...state, scores: { ...state.scores, enabled: v } }); }
  function addTeam() {
    if (!name.trim()) return;
    setState({ ...state, scores: { ...state.scores, teams: [...teams, { id: `t-${Date.now()}`, name: name.trim(), score: 0 }] } });
    setName("");
  }
  function removeTeam(id) { setState({ ...state, scores: { ...state.scores, teams: teams.filter(t => t.id !== id) } }); }
  function changeScore(id, delta) {
    setState({ ...state, scores: { ...state.scores, teams: teams.map(t => t.id === id ? { ...t, score: Math.max(-999, t.score + delta) } : t) } });
  }

  if (!enabled) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div style={panelLabelStyle()}>POENG</div>
        <button onClick={() => toggleEnabled(true)} style={toggleOffBtnStyle()}>
          <span>Slå på poeng</span><span style={{ fontSize: '18px' }}>+</span>
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={panelLabelStyle()}>POENG</span>
        <button onClick={() => toggleEnabled(false)} style={linkBtnStyle()}>Skjul</button>
      </div>
      {teams.map((t) => (
        <div key={t.id} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: COLOR.cardAlt, borderRadius: '10px', padding: '9px 11px'
        }}>
          <span style={{ fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{t.name}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <button onClick={() => changeScore(t.id, -1)} style={miniBtnStyle('minus')}>−</button>
            <span style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '18px', minWidth: '20px', textAlign: 'center' }}>{t.score}</span>
            <button onClick={() => changeScore(t.id, +1)} style={miniBtnStyle('plus')}>+</button>
          </div>
          <button onClick={() => removeTeam(t.id)} style={{ background: 'none', border: 'none', color: COLOR.textSecondary, cursor: 'pointer', fontSize: '12px', marginLeft: '6px' }}>✕</button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: '6px' }}>
        <input style={panelInputStyle({ flex: 1 })} placeholder="Legg til lag…" value={name}
          onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTeam()} />
        <button onClick={addTeam} style={miniBtnStyle('plus')}>+</button>
      </div>
    </div>
  );
}


const ACTIVITIES = [
  { id: "find", name: "Klar–ferdig–finn!", icon: "🔍", family: "energi", line: "Finn noe i rommet som passer oppdraget." },
  { id: "ordjakt", name: "Ordjakt", icon: "📝", family: "ord", line: "Flest mulig ord på én bokstav." },
  { id: "quiz", name: "Quiz", icon: "🧠", family: "kunnskap", line: "Spørsmål med fasit du avdekker selv." },
  { id: "fourlights", name: "Fire lys", icon: "💡", family: "energi", line: "Gjør det skjermen viser, så raskt du kan." },
  { id: "emoji", name: "Emoji-historie", icon: "😀", family: "lek", line: "Lag en historie ut av emojiene du får." },
  { id: "tallkamp", name: "Tallkamp", icon: "🔢", family: "ord", line: "Kom nærmest måltallet med tallene du får." },
  { id: "hvem", name: "Hvem er jeg?", icon: "❓", family: "kunnskap", line: "Hint på hint til noen gjetter person, sted eller ting." },
  { id: "sixplaces", name: "6-plasser", icon: "🎲", family: "energi", line: "Terningen bestemmer hvem som er ute." },
  { id: "dilemma", name: "Dilemma", icon: "⚖️", family: "energi", line: "Velg side i rommet og forklar hvorfor." },
  { id: "hangman", name: "Hangman", icon: "💀", family: "ord", line: "Gjett ordet én bokstav om gangen.", badge: "En elev skriver ordet" },
  { id: "timeline", name: "Tidslinje", icon: "📅", family: "kunnskap", line: "Sett hendelsene i riktig rekkefølge." },
  { id: "fivein30", name: "5 på 30 sekunder", icon: "⏱️", family: "ord", line: "Nevn fem ting i kategorien før tiden går ut." },
  { id: "wheel", name: "Lykkehjul", icon: "🎡", family: "lek", line: "Snurr hjulet og trekk en elev.", badge: "Elevlista" },
  { id: "factmyth", name: "Fakta eller myte", icon: "🤔", family: "kunnskap", line: "Er påstanden sann eller oppspinn?" },
  { id: "emojiface", name: "Emoji-ansikt", icon: "😄", family: "lek", line: "Vis ansiktet på skjermen med kroppen." },
  { id: "jaellernei", name: "Ja eller nei?", icon: "✅", family: "ekstern", line: "Feil svar, og du setter deg.", badge: "Ny fane ↗", external: "https://ja-nei.ak-kreativ.no" },
  { id: "gjettbildet", name: "Gjett bildet", icon: "🖼️", family: "ekstern", line: "Bildet avdekkes bit for bit.", badge: "Ny fane ↗", external: "https://gjett-bildet.ak-kreativ.no" },
  { id: "slange", name: "Slange", icon: "🐍", family: "ord", line: "Bygg en ordkjede sammen, ett ord om gangen." },
];

// Aktiviteter som bruker useCountdown og dermed ER klokka i stolpen mens de er åpne —
// den flytende stoppeklokka skal ikke vises oppå, da ville samme tall stått to steder.
const SELF_TIMED_ACTIVITY_IDS = ["find", "ordjakt", "tallkamp", "fivein30", "slange"];

const FAMILY_STYLES = {
  mørkt: {
    energi: { bg: '#1D1913' },
    ord: { bg: '#141E1C' },
    kunnskap: { bg: '#161A23' },
    lek: { bg: '#1F181D' },
    ekstern: { bg: '#171A1E' }
  },
  lyst: {
    energi: { bg: '#FFE9C2', name: '#4A3208', line: '#6E5220' },
    ord: { bg: '#CFEFE2', name: '#123C30', line: '#2C5A4C' },
    kunnskap: { bg: '#D8E2FF', name: '#1B2A5C', line: '#374878' },
    lek: { bg: '#FBDCE4', name: '#54202F', line: '#743C4C' },
    ekstern: { bg: '#E4E7EC', name: '#282D36', line: '#4A515C' }
  }
};

function familyStyle(theme, family) {
  const f = FAMILY_STYLES[theme]?.[family] || FAMILY_STYLES.mørkt.energi;
  if (theme === "lyst") return { bg: f.bg, name: f.name, line: f.line, badge: f.line, border: 'transparent', shadow: '0 2px 0 rgba(0,0,0,.08)', emojiOpacity: 0.22 };
  return { bg: f.bg, name: COLOR.chromeText, line: '#A7AFBC', badge: '#7C8593', border: 'rgba(255,255,255,.07)', shadow: 'none', emojiOpacity: 0.28 };
}

function HubView() {
  const [state, setState] = useAppState();
  const active = state.classes.find((c) => c.id === state.activeClassId) || state.classes[0];

  function openActivity(id) {
    setState({
      ...state,
      ui: { ...state.ui, view: "activity", activeActivity: id, railPanel: null },
      draw: { ...state.draw, pool: [], current: null, total: 0 }
    });
    if (window.gtag) window.gtag('event', 'aktivitet_start', { aktivitet_navn: id });
  }
  const theme = state.theme === "mørkt" ? "mørkt" : "lyst";
  function setTheme(t) { setState({ ...state, theme: t }); }

  const subtitleParts = [active?.name, `${active?.students?.length || 0} elever`];
  if (state.stopwatch.running) subtitleParts.push(`timer går: ${formatClock(state.stopwatch.remaining)}`);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div style={{
        padding: '24px 32px 18px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px 16px', flex: 'none'
      }}>
        <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '30px', letterSpacing: '-.01em', color: COLOR.textPrimary }}>Aktiviteter</div>
        <div style={{ fontSize: '15px', color: COLOR.textSecondary }}>{subtitleParts.join(' · ')}</div>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: '4px', padding: '4px', border: `1px solid ${COLOR.shellBorder}`, borderRadius: '11px' }} role="radiogroup" aria-label="Tema">
          <button
            role="radio" aria-checked={theme === "lyst"}
            onClick={() => setTheme("lyst")}
            style={{
              border: 'none', borderRadius: '8px', padding: '7px 15px', fontFamily: FONT_UI, fontWeight: 600, fontSize: '14px', cursor: 'pointer',
              background: theme === "lyst" ? '#1E232B' : 'transparent',
              color: theme === "lyst" ? '#FFE9C2' : COLOR.textSecondary
            }}
          >Lyst</button>
          <button
            role="radio" aria-checked={theme === "mørkt"}
            onClick={() => setTheme("mørkt")}
            style={{
              border: 'none', borderRadius: '8px', padding: '7px 15px', fontFamily: FONT_UI, fontWeight: 600, fontSize: '14px', cursor: 'pointer',
              background: theme === "mørkt" ? COLOR.accent : 'transparent',
              color: theme === "mørkt" ? COLOR.accentOnDark : COLOR.textSecondary
            }}
          >Mørkt</button>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 32px 26px', display: 'flex', flexDirection: 'column', gap: '30px' }}>
        <div className="ks-hub-grid" style={{
          display: 'grid', gridAutoRows: 'minmax(134px, auto)', gap: '14px'
        }}>
          {ACTIVITIES.map((a) => {
            const fs = familyStyle(theme, a.family);
            return (
              <button
                key={a.id}
                onClick={() => a.external ? window.open(a.external, '_blank') : openActivity(a.id)}
                style={{
                  position: 'relative', overflow: 'hidden', borderRadius: '16px',
                  padding: '18px 20px', cursor: 'pointer', textAlign: 'left',
                  display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '6px',
                  background: fs.bg, border: `1px solid ${fs.border}`, boxShadow: fs.shadow
                }}
              >
                <span aria-hidden="true" style={{
                  position: 'absolute', top: '-6px', right: '6px', fontSize: '66px', lineHeight: 1, opacity: fs.emojiOpacity
                }}>{a.icon}</span>
                <div style={{
                  fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '26px', lineHeight: 1.08, letterSpacing: '-.015em',
                  paddingRight: '58px', color: fs.name
                }}>{a.name}</div>
                <div style={{ fontSize: '15px', lineHeight: 1.35, paddingRight: '58px', color: fs.line }}>{a.line}</div>
                {a.badge && <div style={{ fontSize: '13px', color: fs.badge }}>{a.badge}</div>}
              </button>
            );
          })}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '21px', letterSpacing: '-.01em', color: COLOR.textPrimary, whiteSpace: 'nowrap' }}>
              Har elevene egne enheter?
            </div>
            <div style={{ flex: 1, height: '1px', background: COLOR.shellBorder }} />
          </div>
          <a href="https://game.ak-kreativ.no" target="_blank" rel="noopener noreferrer" style={{
            display: 'flex', alignItems: 'center', gap: '24px',
            background: COLOR.card, border: `1px solid ${COLOR.shellBorder}`, borderRadius: '16px',
            padding: '22px 26px', textDecoration: 'none', color: 'inherit', transition: 'border-color 0.15s ease'
          }}
          onMouseOver={(e) => { e.currentTarget.style.borderColor = 'rgba(255,176,32,.5)'; }}
          onMouseOut={(e) => { e.currentTarget.style.borderColor = COLOR.shellBorder; }}
          >
            <span aria-hidden="true" style={{ fontSize: '42px', lineHeight: 1, flex: 'none' }}>📱</span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: '25px', letterSpacing: '-.015em', color: COLOR.textPrimary }}>Klassespill</div>
              <div style={{ fontSize: '16px', lineHeight: 1.35, color: COLOR.textSecondary, textWrap: 'pretty' }}>
                Prøv Klassespill: du styrer aktiviteten fra prosjektor, elevene logger inn med romkode og deltar fra hver sin enhet. Noen aktiviteter gir dem en buzzer, andre oppgaver de kappes om å løse.
              </div>
            </div>
            <span aria-hidden="true" style={{ fontSize: '28px', color: COLOR.accent, flex: 'none' }}>↗</span>
          </a>
        </div>
      </div>
    </div>
  );
}

function ActivityView() {
  const [state, setState] = useAppState();
  const randomKey = state.ui.activityKey;
  const [status, setStatus] = useState("");
  const activeId = state.ui.activeActivity;
  const meta = ACTIVITIES.find(a => a.id === activeId);

  useLayoutEffect(() => { setStatus(""); }, [activeId, randomKey]);
  useLayoutEffect(() => {
    if (state.draw.current || state.draw.pool.length || state.draw.total) {
      setState({ ...state, draw: { ...state.draw, pool: [], current: null, total: 0 } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, randomKey]);
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [activeId, randomKey]);

  function backToHub() { setState({ ...state, ui: { ...state.ui, view: "hub", activeActivity: null } }); }

  const panelOpen = !!state.ui.railPanel;
  const draw = state.draw;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, position: 'relative' }}>
      <div style={{
        padding: '12px 22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between'
      }}>
        <button onClick={backToHub} style={quietBtnStyle()}>← Aktiviteter</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <ActivityStatus status={status} />
          {status && <span style={{ color: COLOR.mutedText2 }}>·</span>}
          <span style={{ fontSize: '15px', color: COLOR.textSecondary }}>{meta?.icon} {meta?.name}</span>
        </div>
      </div>

      <div aria-live="polite" style={{
        flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'flex-start', gap: '32px', overflowY: 'auto',
        padding: activeId === "quiz" ? `0 ${panelOpen ? 60 : 90}px 28px` : `min(9vh, 90px) ${panelOpen ? 60 : 90}px 40px`
      }}>
        {draw.current && (
          <button
            onClick={() => setState({ ...state, draw: { ...draw, pool: [], current: null, total: 0 } })}
            title="Klikk for å fjerne"
            style={{
              background: COLOR.drawnPillBg, border: `1px solid ${COLOR.drawnPillBorder}`, borderRadius: '99px',
              padding: '10px 24px 10px 18px', display: 'flex', alignItems: 'center', gap: '14px', cursor: 'pointer'
            }}
            onMouseOver={(e) => { e.currentTarget.style.opacity = '.7'; }}
            onMouseOut={(e) => { e.currentTarget.style.opacity = '1'; }}
          >
            <span style={{ fontSize: '17px', color: COLOR.textSecondary }}>Trukket:</span>
            <span style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '32px', color: COLOR.accentText }}>{draw.current}</span>
            <span aria-hidden="true" style={{ fontSize: '17px', color: COLOR.textSecondary }}>✕</span>
          </button>
        )}

        <div style={{
          width: '100%', display: 'flex', justifyContent: 'center',
          ...(activeId === "quiz" ? { flex: 1, minHeight: 0 } : {})
        }}>
          {activeId === "find" && <FindIt key={randomKey} onStatus={setStatus} />}
          {activeId === "emoji" && <EmojiStory key={randomKey} onStatus={setStatus} />}
          {activeId === "ordjakt" && <WordHunt key={randomKey} onStatus={setStatus} />}
          {activeId === "tallkamp" && <NumberBattle key={randomKey} onStatus={setStatus} />}
          {activeId === "hvem" && <WhoAmI key={randomKey} onStatus={setStatus} />}
          {activeId === "quiz" && <QuickQuiz key={randomKey} onStatus={setStatus} />}
          {activeId === "wheel" && <WheelOfNames key={randomKey} onStatus={setStatus} />}
          {activeId === "dilemma" && <Dilemma key={randomKey} onStatus={setStatus} />}
          {activeId === "sixplaces" && <SixPlaces key={randomKey} onStatus={setStatus} />}
          {activeId === "hangman" && <Hangman key={randomKey} onStatus={setStatus} />}
          {activeId === "fourlights" && <FourLights key={randomKey} onStatus={setStatus} />}
          {activeId === "fivein30" && <FiveIn30 key={randomKey} />}
          {activeId === "emojiface" && <EmojiFace key={randomKey} />}
          {activeId === "timeline" && <Timeline key={randomKey} onStatus={setStatus} />}
          {activeId === "factmyth" && <FactMyth key={randomKey} onStatus={setStatus} />}
          {activeId === "slange" && <SlangeOffline key={randomKey} />}
        </div>
      </div>

      {!panelOpen && !SELF_TIMED_ACTIVITY_IDS.includes(activeId) && <FloatingStopwatch />}
    </div>
  );
}

// --- AKTIVITETER MED FULL FUNKSJONALITET ---

// --- Aktivitet: Slange (offline, laerer-PC) ---
// Egen, uavhengig implementasjon. Roerer ikke slange-spillet i Klassespill/game-repoet.
const SLANGE_MIN_WORD_LENGTH = 2;
const SLANGE_TURN_SECONDS = 20;
const SLANGE_CATEGORIES = [
  "Dyr", "Mat og drikke", "Sport", "Yrker", "Land og byer", "Farger",
  "Skolefag", "Filmer og serier", "Klær", "Møbler og interiør",
  "Musikkinstrumenter", "Kroppsdeler", "Frukt og grønnsaker",
  "Transportmidler", "Eventyr og sagn", "Superhelter", "Dataspill",
  "Værtyper", "Følelser", "Verktøy"
];

function SlangeOffline() {
  const [chain, setChain] = useState([]);
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const [category, setCategory] = useState("");
  const [usedCategories, setUsedCategories] = useState([]);
  const inputRef = useRef(null);
  const timer = useCountdown(SLANGE_TURN_SECONDS);

  function pickCategory() {
    const available = usedCategories.length >= SLANGE_CATEGORIES.length
      ? SLANGE_CATEGORIES
      : SLANGE_CATEGORIES.filter(c => !usedCategories.includes(c));
    const picked = available[randInt(0, available.length - 1)];
    setCategory(picked);
    setUsedCategories(usedCategories.length >= SLANGE_CATEGORIES.length ? [picked] : [...usedCategories, picked]);
  }

  useEffect(() => { pickCategory(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function toggleTimer() {
    if (timer.remaining === 0) timer.restart();
    else if (timer.running) timer.stop();
    else timer.start();
  }

  const lastWord = chain.length ? chain[chain.length - 1] : null;
  const requiredLetter = lastWord ? lastWord.slice(-1).toUpperCase() : null;

  function submitWord(e) {
    e.preventDefault();
    const word = input.trim();
    if (!word) return;
    if (word.length < SLANGE_MIN_WORD_LENGTH) {
      setError("Ordet er for kort.");
      beep(400, 800);
      return;
    }
    const normalized = word.toLocaleUpperCase('nb-NO');

    if (requiredLetter && normalized[0] !== requiredLetter) {
      setError(`Ordet må begynne på "${requiredLetter}"!`);
      beep(400, 800);
      return;
    }
    if (chain.some(w => w.toLocaleUpperCase('nb-NO') === normalized)) {
      setError("Det ordet er allerede brukt i kjeden!");
      beep(400, 800);
      return;
    }

    setChain([...chain, word]);
    setInput("");
    setError("");
    timer.reset();
    beep(200, 1300);
    inputRef.current && inputRef.current.focus();
  }

  function resetChain() {
    if (chain.length > 0 && !window.confirm("Nullstille ordkjeden?")) return;
    setChain([]);
    setInput("");
    setError("");
    timer.reset();
  }

  return (
    <div style={{ textAlign: 'center', width: '100%', maxWidth: '700px' }}>
      <div style={{
        fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '32px', marginBottom: '8px', color: COLOR.textPrimary,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px'
      }}>
        🐍 Slange
      </div>
      <p style={{ color: COLOR.textSecondary, marginBottom: '24px', fontSize: '19px' }}>
        Bruk «Trekk elev» i stolpen for å velge hvem sin tur det er. Eleven sier et ord høyt — skriv det inn under.
      </p>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '14px', flexWrap: 'wrap', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
          <span style={{ fontSize: '13px', fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: COLOR.textSecondary }}>Kategori</span>
          <span style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '22px', color: COLOR.accentText }}>{category}</span>
        </div>
        <button onClick={pickCategory} style={mainSecondaryBtnSmallStyle()}>Ny kategori</button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
        <BareTimer remaining={timer.remaining} total={SLANGE_TURN_SECONDS} />
        <button onClick={toggleTimer} style={mainPrimaryBtnStyle({ padding: '11px 24px', fontSize: '16px', borderRadius: '11px' })}>
          {timer.running ? "Stopp tiden" : "Start tiden"}
        </button>
      </div>

      <div style={{
        background: COLOR.card,
        borderRadius: '16px',
        padding: '20px',
        marginBottom: '20px',
        minHeight: '56px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        alignItems: 'center',
        justifyContent: chain.length ? 'flex-start' : 'center'
      }}>
        {chain.length === 0 ? (
          <span style={{ color: COLOR.mutedText, fontSize: '15px' }}>Kjeden er tom — første ord kan være hva som helst.</span>
        ) : chain.map((w, i) => (
          <span key={i} style={{
            background: i === chain.length - 1 ? COLOR.accent : COLOR.cardHover,
            color: i === chain.length - 1 ? COLOR.accentOnDark : COLOR.textCard,
            padding: '6px 14px',
            borderRadius: '999px',
            fontSize: '16px',
            fontWeight: '600'
          }}>{w}</span>
        ))}
      </div>

      {requiredLetter && (
        <div style={{ fontSize: '18px', color: COLOR.textSecondary, marginBottom: '12px' }}>
          Neste ord må begynne på: <span style={{ fontSize: '28px', fontWeight: 'bold', color: COLOR.accentText }}>{requiredLetter}</span>
        </div>
      )}

      <form onSubmit={submitWord} style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginBottom: '8px', flexWrap: 'wrap' }}>
        <input
          ref={inputRef}
          autoFocus
          value={input}
          onChange={(e) => { setInput(e.target.value); if (error) setError(""); }}
          placeholder={requiredLetter ? `Ord som starter på ${requiredLetter}…` : "Skriv første ord…"}
          style={mainFieldStyle({
            flex: 1, maxWidth: '320px', borderRadius: '12px', padding: '14px 16px', fontSize: '18px',
            border: error ? '2px solid #ef4444' : `1px solid ${COLOR.shellBorder}`
          })}
        />
        <button type="submit" style={mainPrimaryBtnStyle()}>Legg til</button>
      </form>

      {error && (
        <div style={{ color: '#ef4444', fontSize: '15px', fontWeight: '600', marginBottom: '12px' }}>
          ⚠️ {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', alignItems: 'center', marginTop: '20px', flexWrap: 'wrap' }}>
        <div style={{ fontSize: '14px', color: COLOR.textSecondary }}>
          Ord i kjeden: <strong style={{ color: COLOR.textPrimary }}>{chain.length}</strong>
        </div>
        <button style={mainSecondaryBtnSmallStyle()} onClick={resetChain}>Nullstill kjede</button>
      </div>
    </div>
  );
}

// --- Aktivitet: Klar–ferdig–finn!
const FIND_PROMPTS = [
  "noe blått", "noe som lager lyd", "et par sokker", "noe som lukter godt",
  "en bok med bilder", "noe som starter på S", "noe mykt", "noe rundt",
  "noe som kan bære vann", "noe du kan skrive med", "noe laget av tre",
  "noe eldre enn deg", "noe med hjul", "noe som kan brettes",
  "noe rødt", "noe som kan åpnes", "noe kaldt", "noe varmt",
  "noe som glitrer", "noe du kan spise", "noe som starter på M",
  "noe firkantet", "noe smalt", "noe gult", "noe gjennomsiktig",
  "noe med tekst", "noe elektronisk", "noe naturlig", "noe kunstig",
  "noe som kan bevege seg", "noe som er fast", "noe som kan brettes",
  "noe som har hull", "noe som er glatt", "noe som er rufsete",
  "noe grønt", "noe gult", "noe fiolett", "noe blått", "noe hvitt",
  "noe som kan henge", "noe du kan tegne på", "noe som lukter vondt",
  "noe du kan kle på deg", "noe som blinker", "noe som har tall",
  "noe du kan lese", "noe som har knapper", "noe du kan kaste",
  "noe som er hardt", "noe som kan rulle", "noe som er søtt",
  "noe som kan knuses", "noe som kan strekkes", "noe som lager skygge",
  "noe med et dyr på", "noe som lager musikk", "noe du kan åpne en dør med",
  "noe som kan åpnes og lukkes", "noe som er veldig lite", "noe som er veldig stort",
  "noe grønt", "noe gult", "noe fiolett", "noe svart", "noe hvitt"
];

function FindIt() {
  const [started, setStarted] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [usedPrompts, setUsedPrompts] = useState([]);
  const timer = useCountdown(30);

  function pickPrompt() {
    const availablePrompts = usedPrompts.length >= FIND_PROMPTS.length
      ? FIND_PROMPTS
      : FIND_PROMPTS.filter(p => !usedPrompts.includes(p));
    const newPrompt = availablePrompts[randInt(0, availablePrompts.length - 1)];
    setPrompt(newPrompt);
    setUsedPrompts(usedPrompts.length >= FIND_PROMPTS.length ? [newPrompt] : [...usedPrompts, newPrompt]);
  }

  function start() {
    pickPrompt();
    setStarted(true);
  }

  function newAssignment() {
    pickPrompt();
    timer.reset();
  }

  if (!started) {
    return (
      <IntroScreen
        icon="🔍" name="Klar–ferdig–finn!"
        sentence="Finn noe i rommet som passer oppdraget. Første som viser noe som passer, får poenget."
        badges={[{ icon: '⏱', label: '30 sekunder' }, { icon: '🚶', label: 'Elevene kan gå rundt' }]}
        primaryLabel="Vis oppdrag" onPrimary={start} showDraw
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '34px', width: '100%', maxWidth: '900px' }}>
      <div aria-live="polite" style={{
        fontSize: 'clamp(44px, 7vw, 92px)', fontWeight: 800, fontFamily: FONT_DISPLAY,
        textAlign: 'center', lineHeight: 1.03, letterSpacing: '-.025em'
      }}>
        <span style={{ color: COLOR.textSecondary }}>Finn </span>
        <span style={{ color: COLOR.textPrimary }}>{prompt}</span>
      </div>

      <BareTimer remaining={timer.remaining} total={30} />

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
        <TimerActionBar
          running={timer.running} remaining={timer.remaining}
          onStart={timer.start} onStop={timer.stop} onRestart={timer.restart}
          onNext={newAssignment} nextLabel="Nytt oppdrag"
        />
        <div style={{ fontSize: '18px', color: COLOR.textSecondary }}>Første som finner noe, viser det til klassen.</div>
      </div>
    </div>
  );
}

// --- Aktivitet: Emoji-historie
const EMOJIS = [
  '😀', '😅', '😂', '🤣', '😊', '😍', '🤔', '😴', '😎', '😭', '🥳', '🤯', '😬', '😇', '🙃', '🤓', '😡', '👻', '🤖', '🐶', 
  '🐱', '🐸', '🐻', '🦊', '🐼', '🐨', '🐯', '🐧', '🐥', '🦄', '🐝', '🌸', '🍀', '🍎', '🍌', '🍕', '🍔', '🍟', '🍰', '🍫', 
  '🎂', '🍩', '🥐', '☕', '🍵', '🥤', '⚽', '🏀', '🏈', '🎲', '🎯', '🎵', '🎧', '🎸', '🎻', '🎹', '🚲', '🛴', '🚗', '✈️', 
  '🚀', '🏖️', '🏔️', '🌧️', '🌈', '🔥', '⭐', '💡', '⏰', '🎁', '📚', '✏️', '🧩', '🧠', '🌙', '☀️', '❄️', '🌺', '🌻', '🌷', 
  '🦋', '🐠', '🐙', '🐢', '🦉', '🐰', '🐭', '🍓', '🍇', '🍊', '🥕', '🥦', '🍞', '🧀', '🎪', '🎨', '🏠', '🌍', '⛵', '🎯', 
  '🎮', '🎭', '📱', '💻', '🔑', '🎒', '👑', '💎', '🌟', '🏆', '🎸', '🥁', '📷', '🔬', '🛸', '🚂', '🎠', '🍭', '🎈'
];

function EmojiStory() {
  const [started, setStarted] = useState(false);
  const [count, setCount] = useState(3);
  const [items, setItems] = useState([]);

  function draw(n) {
    const useCount = n || count;
    setItems(shuffle(EMOJIS).slice(0, useCount));
    setCount(useCount);
  }

  function start(n) {
    draw(n);
    setStarted(true);
  }

  const countOptions = [{ label: '3 emojier', value: 3 }, { label: '5 emojier', value: 5 }];

  if (!started) {
    return (
      <IntroScreen
        icon="😀" name="Emoji-historie"
        sentence="Lag en setning eller mini-historie som passer til emojiene."
        toggle={{
          value: count, onChange: (n) => setCount(n), options: countOptions, label: "Antall emojier",
          helperText: "Fem emojier gjør oppgaven vanskeligere."
        }}
        primaryLabel="Vis emojier" onPrimary={() => start(count)} showDraw
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '36px', width: '100%', maxWidth: '900px' }}>
      <div aria-live="polite" style={{ fontSize: '120px', lineHeight: 1.1, textAlign: 'center' }}>{items.join(" ")}</div>
      <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
        <button onClick={() => draw()} style={mainPrimaryBtnStyle()}>Nytt oppdrag</button>
        <Bryter value={count} onChange={(n) => draw(n)} options={countOptions} label="Antall emojier" small />
      </div>
    </div>
  );
}

// --- Aktivitet: Ordjakt
function WordHunt() {
  const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZÆØÅ".split("");
  const [started, setStarted] = useState(false);
  const [letter, setLetter] = useState("");
  const timer = useCountdown(60);

  function pickLetter() {
    setLetter(LETTERS[randInt(0, LETTERS.length - 1)]);
  }

  function start() {
    pickLetter();
    setStarted(true);
  }

  function newRound() {
    pickLetter();
    timer.reset();
  }

  if (!started) {
    return (
      <IntroScreen
        icon="📝" name="Ordjakt"
        sentence="Finn flest mulig ord som begynner på bokstaven."
        badges={[{ icon: '⏱', label: '60 sekunder' }]}
        primaryLabel="Vis bokstav" onPrimary={start} showDraw
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '36px', width: '100%', maxWidth: '900px' }}>
      <div aria-live="polite" style={{ fontSize: 'clamp(88px, 12vw, 160px)', fontWeight: 800, fontFamily: FONT_DISPLAY, color: COLOR.accentText }}>{letter}</div>
      <BareTimer remaining={timer.remaining} total={60} />
      <TimerActionBar
        running={timer.running} remaining={timer.remaining}
        onStart={timer.start} onStop={timer.stop} onRestart={timer.restart}
        onNext={newRound} nextLabel="Ny bokstav"
      />
    </div>
  );
}

// --- Delt nedtelling: state (useCountdown), visning (BareTimer) og handlingslinje (TimerActionBar)
// Følger den universelle handlingslinje-regelen (design_handoff_klarferdigfinn/README.md, Del 1):
// høyst to knapper, primær bytter tekst etter tilstand, ingen egen "Nullstill".
// Koblet til klokka i stolpen (state.stopwatch): en aktivitet med tid ER klokka så lenge den er
// åpen, i stedet for to separate tellere. StopwatchTicker (montert på app-roten) driver selve
// nedtellingen og pipet ved 0 — denne hooken bare leser/skriver state.stopwatch.
function useCountdown(total) {
  const [state, setState] = useAppState();
  const { remaining, running } = state.stopwatch;

  useLayoutEffect(() => {
    setState({ ...state, stopwatch: { ...state.stopwatch, duration: total, remaining: total, running: false } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    remaining,
    running,
    start: () => setState({ ...state, stopwatch: { ...state.stopwatch, running: true } }),
    stop: () => setState({ ...state, stopwatch: { ...state.stopwatch, running: false } }),
    restart: () => setState({ ...state, stopwatch: { ...state.stopwatch, remaining: total, running: true } }),
    reset: () => setState({ ...state, stopwatch: { ...state.stopwatch, remaining: total, running: false } })
  };
}

function BareTimer({ remaining, total }) {
  const frac = total > 0 ? Math.max(0, remaining / total) : 0;
  const mm = Math.floor(remaining / 60);
  const ss = String(remaining % 60).padStart(2, '0');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px', width: 'min(600px, 100%)' }}>
      <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '72px', letterSpacing: '-.02em', color: COLOR.accentText }}>{mm}:{ss}</div>
      <div style={{ width: '100%', height: '10px', borderRadius: '99px', background: COLOR.track, overflow: 'hidden' }}>
        <div style={{ width: `${frac * 100}%`, height: '100%', background: COLOR.accent, borderRadius: '99px', transition: 'width 1s linear' }} />
      </div>
    </div>
  );
}

function TimerActionBar({ running, remaining, onStart, onStop, onRestart, onNext, nextLabel = "Nytt oppdrag" }) {
  const isDone = remaining === 0;
  return (
    <div style={{ display: 'flex', gap: '12px' }}>
      {isDone ? (
        <>
          <button onClick={onNext} style={mainPrimaryBtnStyle()}>{nextLabel}</button>
          <button onClick={onRestart} style={mainSecondaryBtnStyle()}>Start tiden igjen</button>
        </>
      ) : running ? (
        <>
          <button onClick={onStop} style={mainPrimaryBtnStyle()}>Stopp tiden</button>
          <button onClick={onNext} style={mainSecondaryBtnStyle()}>{nextLabel}</button>
        </>
      ) : (
        <>
          <button onClick={onStart} style={mainPrimaryBtnStyle()}>Start tiden</button>
          <button onClick={onNext} style={mainSecondaryBtnStyle()}>{nextLabel}</button>
        </>
      )}
    </div>
  );
}

// --- Aktivitet: Tallkamp
function NumberBattle() {
  const [started, setStarted] = useState(false);
  const [targetNumber, setTargetNumber] = useState(0);
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const timer = useCountdown(90);

  function generatePuzzle() {
    const target = randInt(25, 99);
    const count = randInt(5, 6);
    const numbers = [];
    for (let i = 0; i < Math.floor(count / 2); i++) numbers.push(randInt(1, 10));
    for (let i = Math.floor(count / 2); i < count; i++) numbers.push(randInt(10, 25));
    return { target, numbers: shuffle(numbers) };
  }

  function pickPuzzle() {
    const puzzle = generatePuzzle();
    setTargetNumber(puzzle.target);
    setAvailableNumbers(puzzle.numbers);
  }

  function start() {
    pickPuzzle();
    setStarted(true);
  }

  function newRound() {
    pickPuzzle();
    timer.reset();
  }

  if (!started) {
    return (
      <IntroScreen
        icon="🔢" name="Tallkamp"
        sentence="Kom nærmest måltallet ved å kombinere tallene med +, −, × og ÷."
        primaryLabel="Vis utfordring" onPrimary={start}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '36px', width: '100%', maxWidth: '900px' }}>
      <div aria-live="polite" style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '150px', color: COLOR.accentText, lineHeight: 1 }}>{targetNumber}</div>
      <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', justifyContent: 'center' }}>
        {availableNumbers.map((num, index) => (
          <div key={index} style={{
            width: '76px', height: '76px', borderRadius: '14px', background: COLOR.card,
            border: `1px solid ${COLOR.shellBorder}`, display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '30px', color: COLOR.textCard
          }}>{num}</div>
        ))}
      </div>
      <BareTimer remaining={timer.remaining} total={90} />
      <TimerActionBar
        running={timer.running} remaining={timer.remaining}
        onStart={timer.start} onStop={timer.stop} onRestart={timer.restart}
        onNext={newRound} nextLabel="Ny utfordring"
      />
    </div>
  );
}

// --- Aktivitet: Hvem er jeg?
// Fuzzy svar-sjekk, samme metode (Levenshtein-similaritet + inneholder-med-lengdeforhold)
// som Klassespill/game sin Gjett bildet bruker for elevsvar - her strippes i tillegg
// ledende artikkel ("en"/"et") og parentetisk tillegg ("(AI)") siden fasiten i denne
// aktiviteten skrives som en hel setning, ikke bare selve ordet.
function normalizeWhoAmIAnswer(s) {
  return s.trim().toLowerCase()
    .replace(/\([^)]*\)/g, '')
    .replace(/^(en|et|den|det)\s+/i, '')
    .trim();
}
function whoAmISimilarity(s1, s2) {
  let longer = s1, shorter = s2;
  if (s1.length < s2.length) { longer = s2; shorter = s1; }
  if (longer.length === 0) return 1.0;
  const editDistance = ((str1, str2) => {
    const costs = [];
    for (let i = 0; i <= str1.length; i++) {
      let lastValue = i;
      for (let j = 0; j <= str2.length; j++) {
        if (i === 0) costs[j] = j;
        else if (j > 0) {
          let newValue = costs[j - 1];
          if (str1.charAt(i - 1) !== str2.charAt(j - 1)) newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
          costs[j - 1] = lastValue;
          lastValue = newValue;
        }
      }
      if (i > 0) costs[str2.length] = lastValue;
    }
    return costs[str2.length];
  })(longer, shorter);
  return (longer.length - editDistance) / longer.length;
}
function isWhoAmIGuessCorrect(guess, answer) {
  const g = normalizeWhoAmIAnswer(guess);
  const a = normalizeWhoAmIAnswer(answer);
  if (!g) return false;
  if (g === a) return true;
  if (a.includes(g) && g.length / a.length >= 0.5) return true;
  return whoAmISimilarity(g, a) >= 0.75;
}

function WhoAmI() {
  const [state] = useAppState();
  const [topic, setTopic] = useState(null);
  const [step, setStep] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [wonByGuess, setWonByGuess] = useState(false);
  const [usedTopics, setUsedTopics] = useState([]);
  const [guessValue, setGuessValue] = useState("");
  const [guessWrong, setGuessWrong] = useState(false);
  const theme = state.theme === "mørkt" ? "mørkt" : "lyst";

 const WHO_AM_I_TOPICS = [
  // --- Oppdaterte og mer utfordrende versjoner ---
  { answer: "En tannbørste", hints: ["Jeg har et hode, men ingen hjerne", "Jeg har mange hår, men er ikke et dyr", "Jeg trives best på badet", "Jeg jobber best med krem", "Min jobb er å fjerne spor etter mat"] },
 { answer: "En luftballong", hints: ["Jeg har ingen vinger, men kan likevel fly", "Jeg er avhengig av varme for å stige til værs", "Jeg reiser dit vinden tar meg", "Jeg har en kurv der folk kan stå", "Jeg drives av en stor flamme"] },
  { answer: "Et kompass", hints: ["Jeg har et ansikt, men ingen øyne", "Jeg vet alltid veien", "Jeg fungerer ikke godt nær store metallgjenstander", "Jeg er en turgåers beste venn når teknologien svikter", "Min nål peker alltid mot nord"] },
  
  // --- Klassikere (med justerte hint) ---
  { answer: "Pizza", hints: ["Jeg er en matrett", "Jeg kommer fra Napoli", "Jeg er ofte rund og deles i 'stykker'", "Mange liker meg best med smeltet ost på toppen", "Ananas på meg er en evig diskusjon"] },
  { answer: "Harry Potter", hints: ["Jeg er en fiktiv person", "Jeg er kjent for et arr i pannen", "Jeg har en ugle som kjæledyr", "Jeg går på en spesiell skole for magi", "Min største fiende er 'han-du-vet'"] },
{ answer: "Popcorn", hints: ["Jeg er mat", "Jeg lager lyd når jeg lages", "Jeg er vanlig på kino", "Jeg er laget av mais", "Jeg popper i varmen"] },

  // --- Finurlige Dyr & Natur ---
  { answer: "Kameleonen", hints: ["Jeg er kjent for å ikke skille meg ut", "Øynene mine kan se i to retninger samtidig", "Tungen min kan være lengre enn kroppen min", "Mange tror jeg skifter farge for å gjemme meg, men det er for å vise følelser", "Jeg er en type øgle"] },
  { answer: "Blekksprekk (en åttearmet)", hints: ["Jeg har tre hjerter og blått blod", "Hvis jeg mister en arm, vokser den ut igjen", "Jeg kan endre både farge og form på sekunder", "Jeg er smartere enn du tror og kan løse komplekse problemer", "Jeg bruker blekk som et røykteppe for å unnslippe fiender"] },
  { answer: "Et ekko", hints: ["Jeg snakker, men har ingen munn", "Jeg svarer alltid, men sier ikke noe nytt", "Jo lenger unna du er, jo lengre tid tar det før jeg svarer", "Jeg trives best i store, tomme rom eller i fjellet", "Jeg gjentar det siste du sa"] },
  { answer: "En vulkan", hints: ["Jeg kan være i dvale i hundrevis av år", "Jeg har et temperament som kan være eksplosivt", "Jeg kan skape nye landområder", "Jeg spyr ut aske og flytende stein", "Jeg ser ut som et fjell, men har et krater på toppen"] },

  // --- Ting & Tang (med feller) ---
  { answer: "Et speil", hints: ["Jeg har ingen munn, men kan 'svare' deg", "Jeg viser deg verden, men alltid baklengs", "Hvis du smiler til meg, smiler jeg alltid tilbake", "Jeg kan knuses i tusen biter", "Jeg viser deg alt, unntatt meg selv"] },
  { answer: "Et frimerke", hints: ["Jeg reiser verden rundt, men står alltid i et hjørne", "Jeg har ofte bilde av en konge eller natur", "Jeg må slikkes på ryggen for å gjøre jobben min", "Jeg hjelper brevet ditt med å komme frem", "Jeg settes øverst til høyre på en konvolutt"] },
  { answer: "En heis", hints: ["Jeg har dører, men er ikke et rom", "Jeg beveger meg bare i to retninger: opp og ned", "Jeg har knapper for hver etasje", "Jeg spiller ofte musikk mens du venter", "Jeg er en raskere måte å komme seg opp i en bygning på enn trapper"] },
  { answer: "En nøkkel", hints: ["Jeg har et hode og tenner, men kan ikke bite", "Jeg passer bare til ett bestemt sted", "Jeg kan åpne det som er låst", "Mister du meg, kan det bli et stort problem", "De fleste har meg i lomma eller på et knippe"] },

  // --- Konsepter & Teknologi ---
  { answer: "Tid", hints: ["Jeg kan gå, men har ingen føtter", "Alle har meg, men ingen kan eie meg", "Jeg kan måles, men ikke sees eller tas på", "Jeg kan 'fly' når du har det gøy", "Noen sier jeg leger alle sår"] },
  { answer: "En skygge", hints: ["Jeg følger deg overalt i solen, men forsvinner i mørket", "Jeg er alltid like stor eller større enn deg", "Du kan ikke fange meg, uansett hvor fort du løper", "Jeg har ingen vekt og lager ingen lyd", "Jeg blir lengst når lyset er lavest"] },
  { answer: "Internett", hints: ["Jeg er et sted uten en fysisk adresse", "Jeg inneholder nesten all kunnskap menneskeheten har samlet", "Jeg er et 'nett' som fanger informasjon, ikke fisk", "Uten meg ville smarttelefonen din vært ganske kjedelig", "Du bruker meg akkurat nå"] },
  { answer: "Kunstig intelligens (AI)", hints: ["Jeg kan skrive en historie, men har ingen fantasi", "Jeg kan lage et bilde, men har aldri holdt en pensel", "Jeg lærer av enorme mengder data", "Mange er både spente og litt redde for fremtiden min", "ChatGPT er en av mine mest kjente slektninger"] },
{ answer: "En drone", hints: ["Jeg kan fly, men har ingen pilot om bord", "Jeg blir ofte brukt til å ta bilder og video fra luften", "Jeg har fire eller flere propeller som snurrer fort", "Jeg styres med en fjernkontroll, ofte med en skjerm", "Jeg kan levere pakker eller brukes til overvåkning"] },
  
  // --- Norske Spesialiteter ---
  { answer: "Ostehøvelen", hints: ["Jeg er en norsk oppfinnelse fra 1925", "Jeg er skarp, men du kutter deg sjelden på meg", "Jeg brukes ofte til frokost og lunsj", "Jeg gjør flate ting enda flatere", "Jeg elsker brunost og norvegia"] },
  { answer: "Bunad", hints: ["Jeg er et klesplagg, men brukes bare ved spesielle anledninger", "Jeg kan fortelle hvor i Norge du eller familien din kommer fra", "Jeg er ofte arvegods og veldig kostbar", "Mange har på seg meg på 17. mai", "Jeg har ofte mye broderier og sølv"] },
  { answer: "Et troll", hints: ["Jeg er en figur fra norsk folketro", "Jeg bor i fjellet eller i mørke skoger", "Jeg tåler ikke sollys", "Jeg har ofte en stor nese og hale", "Noen ganger har jeg flere hoder"] },
  { answer: "Nordlyset", hints: ["Jeg er et lysfenomen på himmelen", "Jeg kan bare sees når det er mørkt og klart", "Jeg danser i grønne, lilla og rosa farger", "Jeg trives best langt mot nord", "Jeg skapes av partikler fra sola som treffer jordas atmosfære"] },

// Kjente personer/figurer
  { answer: "Albert Einstein", hints: ["Jeg er en person", "Jeg var vitenskapsmann", "Jeg hadde bustete hår", "Jeg utviklet relativitetsteorien", "Mitt etternavn brukes som synonym for 'smart'"] },
{ answer: "Mona Lisa", hints: ["Jeg er et kunstverk", "Jeg er et maleri", "Jeg smiler svakt", "Jeg henger i Louvre", "Jeg ble malt av Leonardo da Vinci"] },

// --- Geografi & Steder --- 
{ answer: "Amazonaselven", hints: ["Jeg er en elv", "Jeg renner gjennom en enorm regnskog", "Jeg har flere fiskearter enn noe annet elvesystem", "Jeg ligger i Sør-Amerika", "Jeg er verdens største elv målt i vannmengde"] }, 
{ answer: "Sahara", hints: ["Jeg er et geografisk område", "Jeg er ekstremt tørr og varm", "Jeg dekker store deler av Nord-Afrika", "Jeg er full av sand, men også stein og fjell", "Jeg er verdens største varme ørken"] }, 
{ answer: "Mount Everest", hints: ["Jeg er et sted på jorden", "Luften er veldig tynn på toppen av meg", "Mange drømmer om å bestige meg, men det er farlig", "Jeg ligger i fjellkjeden Himalaya", "Jeg er verdens høyeste fjell"] },
];

 function pickTopic() {
    const availableTopics = usedTopics.length >= WHO_AM_I_TOPICS.length
      ? WHO_AM_I_TOPICS
      : WHO_AM_I_TOPICS.filter(t => !usedTopics.find(used => used.answer === t.answer));
    const picked = availableTopics[randInt(0, availableTopics.length - 1)];
    setTopic(picked);
    setStep(0);
    setRevealed(false);
    setWonByGuess(false);
    setGuessValue("");
    setGuessWrong(false);
    setUsedTopics(usedTopics.length >= WHO_AM_I_TOPICS.length ? [picked] : [...usedTopics, picked]);
  }

  function nextHint() {
    if (topic && step < topic.hints.length - 1) {
      setStep(step + 1);
      setGuessValue("");
      setGuessWrong(false);
    }
  }
  function showAnswer() { setRevealed(true); }
  function checkGuess() {
    if (!topic || !guessValue.trim()) return;
    if (isWhoAmIGuessCorrect(guessValue, topic.answer)) {
      setRevealed(true);
      setWonByGuess(true);
      setGuessWrong(false);
    } else {
      setGuessWrong(true);
      setGuessValue("");
    }
  }

  if (!topic) {
    return (
      <IntroScreen
        icon="❓" name="Hvem er jeg?"
        sentence="Gjett hva det er basert på hintene!"
        badges={[{ label: '5 hint' }]}
        primaryLabel="Vis første hint" onPrimary={pickTopic} showDraw
      />
    );
  }

  const isLast = step === topic.hints.length - 1;
  const logHints = revealed ? topic.hints : topic.hints.slice(0, step);

  const dotColors = {
    done: theme === "mørkt" ? { bg: '#252C38', border: 'transparent', text: '#D5DAE3' } : { bg: '#EFE7DA', border: 'transparent', text: '#3E4653' },
    current: { bg: '#FFB020', border: '#FFB020', text: '#0a0c10' },
    upcoming: theme === "mørkt" ? { bg: 'transparent', border: 'rgba(255,255,255,.16)', text: '#7C8593' } : { bg: 'transparent', border: 'rgba(0,0,0,.16)', text: '#6B7280' }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '30px', width: '100%', maxWidth: '1000px' }}>
      {/* Fast høyde uansett hvor mange tidligere hint som er avdekket (0 til 5) - ellers
          skyver denne loggen hoveddisplayet nedover for hvert "Neste hint"-klikk. Holdt
          bevisst lav (rommer ~1 tidligere hint) i stedet for høy nok til alle fire - en
          høy reservert boks var like ille i praksis: den dyttet selve hint-1-visningen
          langt ned med det samme, rett etter en kompakt IntroScreen, og opplevdes som
          "et stort hopp med en gang". Nyeste hint bunn-forankret + scroll for resten,
          samme mønster som brukt for buzzerkøen i Gjett bildet (Klassespill/game). */}
      <div style={{
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'flex-start',
        gap: '12px', width: '100%', maxWidth: '860px', alignSelf: 'center',
        height: '90px', overflowY: 'auto'
      }}>
        {logHints.map((hint, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: '14px' }}>
            <span style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '19px', color: COLOR.textSecondary, minWidth: '18px' }}>{i + 1}</span>
            <span style={{ fontSize: revealed ? '24px' : '26px', lineHeight: 1.3, color: COLOR.textSecondary, textWrap: 'pretty' }}>{hint}</span>
          </div>
        ))}
      </div>

      {/* minHeight reservert for den STØRSTE denne visningen kan bli (2 linjer på det
          største clamp()-trinnet) - uten dette flytter dots/knapper/svarfelt seg opp
          eller ned avhengig av om nettopp DETTE hintet/svaret er kort nok til å få
          plass på én linje eller bryter til to. Egen (litt større) reservasjon for
          avslørt svar, siden den bruker en større fontstørrelse enn selve hintene. */}
      <div aria-live="polite" style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '18px',
        minHeight: revealed ? '250px' : '185px'
      }}>
        {!revealed ? (
          <>
            <div style={{ fontSize: '14px', fontWeight: 600, letterSpacing: '.09em', color: COLOR.accentText }}>Hint {step + 1} av {topic.hints.length}</div>
            <div style={{
              fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 'clamp(34px, 5vw, 64px)', lineHeight: 1.08,
              letterSpacing: '-.02em', textAlign: 'center', textWrap: 'balance', maxWidth: '960px', color: COLOR.textPrimary
            }}>{topic.hints[step]}</div>
          </>
        ) : (
          <>
            <div style={{ fontSize: '14px', fontWeight: 600, letterSpacing: '.09em', color: wonByGuess ? '#16a34a' : COLOR.textSecondary }}>
              {wonByGuess ? '🎉 RIKTIG SVAR' : 'SVARET'}
            </div>
            <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 'clamp(44px, 7vw, 88px)', letterSpacing: '-.025em', color: COLOR.accentText }}>
              {topic.answer}
            </div>
          </>
        )}
      </div>

      <div aria-hidden="true" style={{ display: 'flex', gap: '10px' }}>
        {topic.hints.map((_, i) => {
          const c = revealed ? dotColors.done : (i < step ? dotColors.done : i === step ? dotColors.current : dotColors.upcoming);
          return (
            <div key={i} style={{
              width: '42px', height: '42px', borderRadius: '12px',
              background: c.bg, border: `1px solid ${c.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '18px', color: c.text
            }}>{i + 1}</div>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: '12px' }}>
        {revealed ? (
          <button onClick={pickTopic} style={mainPrimaryBtnStyle()}>Nytt oppdrag</button>
        ) : isLast ? (
          <>
            <button onClick={showAnswer} style={mainPrimaryBtnStyle()}>Vis svaret</button>
            <button onClick={pickTopic} style={mainSecondaryBtnStyle()}>Nytt oppdrag</button>
          </>
        ) : (
          <>
            <button onClick={nextHint} style={mainPrimaryBtnStyle()}>Neste hint</button>
            <button onClick={showAnswer} style={mainSecondaryBtnStyle()}>Vis svaret</button>
          </>
        )}
      </div>

      {!revealed && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', width: '100%', maxWidth: '380px' }}>
          <div style={{ width: '100%', height: '1px', background: COLOR.shellBorder }} />
          <div style={{ fontSize: '14px', color: COLOR.textSecondary }}>Eller skriv svaret:</div>
          <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
            <input
              value={guessValue}
              onChange={(e) => { setGuessValue(e.target.value); if (guessWrong) setGuessWrong(false); }}
              onKeyDown={(e) => e.key === "Enter" && checkGuess()}
              placeholder="Skriv svaret her …"
              style={mainFieldStyle({ flex: 1, textAlign: 'center', fontSize: '17px', borderColor: guessWrong ? '#ef4444' : undefined })}
            />
            <button onClick={checkGuess} style={mainSecondaryBtnStyle({ padding: '11px 20px', fontSize: '15px', flex: 'none' })}>Sjekk svar</button>
          </div>
          <div style={{ fontSize: '13px', color: '#ef4444', minHeight: '16px' }}>{guessWrong ? "Feil svar — prøv igjen." : ""}</div>
        </div>
      )}
    </div>
  );
}

// --- Aktivitet: Quiz
function quizQuestionFontSize(len) {
  if (len <= 45) return 'max(34px, min(76px, 11cqh))';
  if (len <= 80) return 'max(32px, min(64px, 9.5cqh))';
  if (len <= 130) return 'max(30px, min(52px, 8cqh))';
  return 'max(28px, min(42px, 6.5cqh))';
}
function quizAnswerFontSize(len) {
  if (len <= 14) return '64px';
  if (len <= 24) return '48px';
  return '36px';
}

function QuickQuiz({ onStatus }) {
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [usedQuestions, setUsedQuestions] = useState([]);

 const QUIZ_QUESTIONS = [
    { question: "Hvilket element har det kjemiske symbolet O?", answer: "Oksygen" },
    { question: "Hvor mange planeter er det i solsystemet?", answer: "8 planeter" },
    { question: "Hva heter den største planeten i solsystemet?", answer: "Jupiter" },
    { question: "Hvor mange ben har en edderkopp?", answer: "8 ben" },
    { question: "Hva er det største dyret i verden?", answer: "Blåhval" },
    { question: "Hvor mange kontinenter er det?", answer: "7 kontinenter" },
    { question: "Hvilken planet er kjent som den røde planeten?", answer: "Mars" },
    { question: "Hva kalles en vinkel på 90 grader?", answer: "Rett vinkel" },
    { question: "Hvilken gass utgjør størstedelen av atmosfæren?", answer: "Nitrogen" },
    { question: "Hva er det hardeste naturlige stoffet?", answer: "Diamant" },
    { question: "Hvor mange timer er det i en uke?", answer: "168 timer" },
    { question: "Hva er Norges nasjonalfugl?", answer: "Fossekallen" },
    { question: "Hvilken prosess bruker planter for å lage mat?", answer: "Fotosyntese" },
    { question: "Hva er den minste knokkelen i menneskekroppen?", answer: "Stigbøylen" },
    { question: "Hvor mange gram er det i et kilogram?", answer: "1000 gram" },
    { question: "I hvilket land ligger de kjente pyramidene ved Giza?", answer: "Egypt" },
    { question: "Hvilket hav er det største i verden?", answer: "Stillehavet" },
    { question: "Hvilken norsk konge ble drept i slaget på Stiklestad i 1030?", answer: "Olav den Hellige" },
    { question: "Hvem malte det berømte maleriet 'Skrik'?", answer: "Edvard Munch" },
    { question: "Hva heter hovedstaden i Australia?", answer: "Canberra" },
    { question: "Hvilket grunnstoff har det kjemiske symbolet Fe?", answer: "Jern" },
    { question: "Hva heter den lengste elva i verden?", answer: "Nilen" },
    { question: "Hvor mange dager er det i et skuddår?", answer: "366 dager" },
    { question: "Hvilket land har flest innbyggere i verden?", answer: "India" },
    { question: "Hva kalles studien av jordskjelv?", answer: "Seismologi" },
    { question: "Hvilket metall har det kjemiske symbolet Au?", answer: "Gull" },
    { question: "Hvilken enhet måles elektrisk strøm i?", answer: "Ampere" },
    { question: "Hva heter den største innsjøen i Norge?", answer: "Mjøsa" },
    { question: "Hvilket språk snakkes i Brasil?", answer: "Portugisisk" },
    { question: "Hva er hovedstaden i Canada?", answer: "Ottawa" },

    // Nye spørsmål
    { question: "Hva heter Norges lengste fjord?", answer: "Sognefjorden" },
    { question: "Hva er 12 x 8?", answer: "96" },
    { question: "Hvilket dyr kalles 'skogens konge' i Norge?", answer: "Elg" },
    { question: "Hvem skrev 'Romeo og Julie'?", answer: "William Shakespeare" },
    { question: "Hva heter den største verdensdelen?", answer: "Asia" },
    { question: "Hvilken planet ligger nærmest sola?", answer: "Merkur" },
    { question: "Hvor mange grader er det i en sirkel?", answer: "360 grader" },
    { question: "Hva er navnet på verdens høyeste fjell?", answer: "Mount Everest" },
    { question: "Hva heter den største øya i verden?", answer: "Grønland" },
    { question: "Hva er hovedstaden i Japan?", answer: "Tokyo" },
    { question: "Hvilken sport er Lionel Messi kjent for?", answer: "Fotball" },
    { question: "Hva heter den lengste elva i Norge?", answer: "Glomma" },
    { question: "Hva er hovedstaden i Frankrike?", answer: "Paris" },
    { question: "Hva heter verdens nest største hav?", answer: "Atlanterhavet" },
    { question: "Hva kalles læren om stjerner og planeter?", answer: "Astronomi" }
];

function getNewQuestion() {
    const availableQuestions = usedQuestions.length >= QUIZ_QUESTIONS.length
      ? QUIZ_QUESTIONS
      : QUIZ_QUESTIONS.filter(q => !usedQuestions.find(used => used.question === q.question));
    const question = availableQuestions[randInt(0, availableQuestions.length - 1)];
    setCurrentQuestion(question);
    setShowAnswer(false);
    setUsedQuestions(usedQuestions.length >= QUIZ_QUESTIONS.length ? [question] : [...usedQuestions, question]);
  }

  useEffect(() => {
    if (onStatus) onStatus(currentQuestion ? { label: "Spørsmål", value: `${usedQuestions.length}`, suffix: ` av ${QUIZ_QUESTIONS.length}` } : "");
  }, [currentQuestion, usedQuestions.length, onStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!currentQuestion) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
        <IntroScreen
          icon="🧠" name="Quiz"
          sentence="Klar for et spørsmål?"
          primaryLabel="Vis spørsmål" onPrimary={getNewQuestion} showDraw
        />
      </div>
    );
  }

  const answerFontSize = quizAnswerFontSize(currentQuestion.answer.length);

  return (
    <div style={{
      width: '100%', flex: '1', minHeight: 0, display: 'grid',
      gridTemplateRows: 'minmax(0,1fr) 150px 88px',
      alignItems: 'center', justifyItems: 'center',
      containerType: 'size'
    }}>
      <div style={{ display: 'flex', minHeight: 0, maxHeight: '100%', maxWidth: '940px', alignItems: 'center' }}>
        <div aria-live="polite" style={{
          fontFamily: FONT_DISPLAY, fontWeight: 800,
          fontSize: quizQuestionFontSize(currentQuestion.question.length),
          lineHeight: currentQuestion.question.length <= 45 ? 1.08 : 1.1,
          letterSpacing: '-.02em', textAlign: 'center', textWrap: 'balance', color: COLOR.textPrimary
        }}>{currentQuestion.question}</div>
      </div>

      <div aria-live="polite" style={{
        width: '760px', maxWidth: '100%', height: '140px', borderRadius: '18px',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: showAnswer ? '8px' : '10px',
        background: showAnswer ? 'rgba(255,176,32,.1)' : 'transparent',
        border: showAnswer ? '1px solid rgba(255,176,32,.4)' : '1px dashed rgba(255,255,255,.16)'
      }}>
        <span style={{ fontSize: '14px', fontWeight: 600, letterSpacing: '.09em', color: showAnswer ? '#C99A45' : '#7F899A' }}>SVAR</span>
        {showAnswer ? (
          <span style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: answerFontSize, letterSpacing: '-.02em', color: '#FFB020' }}>
            {currentQuestion.answer}
          </span>
        ) : (
          <span style={{ fontSize: '21px', fontWeight: 400, color: '#8C95A4' }}>Skjult til du trykker «Vis svaret»</span>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '250px 250px', gap: '14px' }}>
        {showAnswer ? (
          <>
            <button onClick={getNewQuestion} style={mainPrimaryBtnStyle({ width: '100%', padding: '16px 0' })}>Nytt spørsmål</button>
            <button onClick={() => setShowAnswer(false)} style={mainSecondaryBtnStyle({ width: '100%', padding: '16px 0' })}>Skjul svaret</button>
          </>
        ) : (
          <>
            <button onClick={() => setShowAnswer(true)} style={mainPrimaryBtnStyle({ width: '100%', padding: '16px 0' })}>Vis svaret</button>
            <button onClick={getNewQuestion} style={mainSecondaryBtnStyle({ width: '100%', padding: '16px 0' })}>Nytt spørsmål</button>
          </>
        )}
      </div>
    </div>
  );
}

// --- Aktivitet: Lykkehjul
const WHEEL_FAMILIES = ["energi", "ord", "kunnskap"];
function wheelLabelFontSize(len) {
  if (len <= 6) return 30;
  if (len <= 9) return 26;
  if (len <= 13) return 22;
  if (len <= 18) return 17;
  return 14;
}
function wheelColorIndices(n) {
  const idx = Array.from({ length: n }, (_, i) => i % WHEEL_FAMILIES.length);
  if (n > 2 && idx[n - 1] === idx[0]) {
    const usedByNeighbors = new Set([idx[n - 2], idx[0]]);
    for (let c = 0; c < WHEEL_FAMILIES.length; c++) {
      if (!usedByNeighbors.has(c)) { idx[n - 1] = c; break; }
    }
  }
  return idx;
}

function WheelOfNames() {
  const [state] = useAppState();
  const theme = state.theme === "mørkt" ? "mørkt" : "lyst";
  const active = state.classes.find((c) => c.id === state.activeClassId) || state.classes[0];
  const [pool, setPool] = useState([]);
  const [drawn, setDrawn] = useState([]);
  const [out, setOut] = useState([]);
  const [rot, setRot] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [winner, setWinner] = useState(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    setPool(active?.students || []);
    setDrawn([]);
    setOut([]);
    setRot(0);
    setSpinning(false);
    setWinner(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id, active?.students]);

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  function spin(sourcePool) {
    const list = sourcePool || pool;
    if (spinning || !list.length) return;
    const n = list.length, per = 360 / n;
    const idx = randInt(0, n - 1);
    const delta = 360 * 4 + ((((-idx * per - rot) % 360) + 360) % 360);
    setSpinning(true);
    setWinner(null);
    setRot(rot + delta);
    timeoutRef.current = setTimeout(() => {
      setSpinning(false);
      setWinner(list[idx]);
      beep(300, 1200);
    }, 3400);
  }

  function takeOutAndSpinAgain() {
    if (!winner) return;
    const newPool = pool.filter(s => s !== winner);
    setPool(newPool);
    setDrawn([...drawn, winner]);
    setWinner(null);
    spin(newPool);
  }

  function keepAndSpinAgain() {
    setWinner(null);
    spin(pool);
  }

  function takeOutManually(name) {
    if (spinning) return;
    setPool(pool.filter(s => s !== name));
    setOut([...out, name]);
  }

  function putBack(name) {
    if (spinning) return;
    setOut(out.filter(s => s !== name));
    setPool([...pool, name]);
  }

  function resetAll() {
    if (spinning) return;
    setPool([...(active?.students || [])]);
    setDrawn([]);
    setOut([]);
    setWinner(null);
  }

  const totalStudents = active?.students?.length || 0;

  if (!totalStudents) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
        <div style={{ fontSize: '24px', color: COLOR.textSecondary, textAlign: 'center' }}>
          Ingen elever i klassen ennå. Legg til elever under «Klasse» i stolpen.
        </div>
      </div>
    );
  }

  const per = pool.length ? 360 / pool.length : 0;
  const colorIdx = wheelColorIndices(pool.length);
  const sliceStyles = colorIdx.map(ci => familyStyle(theme, WHEEL_FAMILIES[ci]));
  const gradient = pool.length
    ? `conic-gradient(from -${(per / 2).toFixed(3)}deg, ${pool.map((_, i) => {
        return `${sliceStyles[i].bg} ${(i * per).toFixed(3)}deg ${((i + 1) * per).toFixed(3)}deg`;
      }).join(', ')})`
    : COLOR.card;

  function handleWheelKey(e) {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); spin(); }
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '36px', width: '100%', padding: '0 30px 24px 40px' }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '18px' }}>
        <div style={{ height: '50px', flex: 'none', display: 'flex', alignItems: 'center' }}>
          {winner && !spinning && (
            <div style={{
              background: COLOR.card, border: '1px solid rgba(255,176,32,.3)', borderRadius: '99px',
              padding: '8px 22px 8px 18px', display: 'flex', alignItems: 'center', gap: '14px'
            }} aria-live="polite">
              <span style={{ fontSize: '16px', color: COLOR.textSecondaryOnPanel }}>Hjulet stoppet på</span>
              <span style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '28px', color: COLOR.accentText, lineHeight: 1 }}>{winner}</span>
            </div>
          )}
        </div>

        {pool.length > 0 ? (
          <div
            role="button"
            tabIndex={0}
            onClick={() => spin()}
            onKeyDown={handleWheelKey}
            aria-label="Snurr hjulet"
            style={{ position: 'relative', width: 'min(560px, 62vh)', height: 'min(560px, 62vh)', cursor: spinning ? 'default' : 'pointer' }}
          >
            <div style={{
              position: 'absolute', top: '-6px', left: '50%', transform: 'translateX(-50%)', zIndex: 3,
              width: 0, height: 0, borderLeft: '17px solid transparent', borderRight: '17px solid transparent',
              borderTop: `30px solid ${COLOR.textPrimary}`, filter: 'drop-shadow(0 4px 10px rgba(0,0,0,.6))'
            }} />
            <div style={{
              position: 'absolute', inset: 0, borderRadius: '50%',
              boxShadow: `0 24px 60px rgba(0,0,0,.55), 0 0 0 10px ${COLOR.railBg}, 0 0 0 11px rgba(255,255,255,.1)`,
              background: gradient,
              transform: `rotate(${rot}deg)`,
              transition: spinning ? 'transform 3.4s cubic-bezier(.16,.84,.16,1)' : 'none'
            }}>
              {pool.map((student, i) => {
                const angle = i * per;
                return (
                  <div key={student} style={{
                    position: 'absolute', left: '50%', top: '50%',
                    transformOrigin: '0 0',
                    transform: `rotate(${angle}deg) translate(0,-180px) rotate(${-angle}deg) translate(-50%,-50%)`,
                    whiteSpace: 'nowrap', fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: `${wheelLabelFontSize(student.length)}px`,
                    color: sliceStyles[i].name
                  }}>{student}</div>
                );
              })}
            </div>
            <div style={{
              position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)',
              width: '104px', height: '104px', borderRadius: '50%', background: COLOR.railBg,
              border: '1px solid rgba(255,255,255,.12)', boxShadow: '0 8px 24px rgba(0,0,0,.5)', zIndex: 2,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '34px', color: COLOR.accent
            }}>{pool.length}</div>
          </div>
        ) : (
          <div style={{
            width: 'min(560px, 62vh)', height: 'min(560px, 62vh)', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: '20px'
          }}>
            <div style={{ fontSize: '24px', color: COLOR.textSecondaryOnPanel }}>Alle er trukket</div>
            <button onClick={resetAll} style={mainPrimaryBtnStyle()}>Legg alle tilbake</button>
          </div>
        )}

        <div style={{ height: '64px', flex: 'none', display: 'flex', alignItems: 'center', gap: '12px' }}>
          {pool.length > 0 && (
            winner && !spinning ? (
              <>
                <button onClick={takeOutAndSpinAgain} style={mainPrimaryBtnStyle({ whiteSpace: 'nowrap', padding: '15px 28px', fontSize: '18px', borderRadius: '13px' })}>
                  Ta {winner} ut og snurr igjen
                </button>
                <button onClick={keepAndSpinAgain} style={mainSecondaryBtnStyle({ whiteSpace: 'nowrap', padding: '15px 28px', fontSize: '18px', borderRadius: '13px' })}>
                  La {winner} stå og snurr igjen
                </button>
              </>
            ) : (
              <button onClick={() => spin()} disabled={spinning} style={mainPrimaryBtnStyle({
                padding: '17px 44px', fontSize: '21px', borderRadius: '14px',
                opacity: spinning ? 0.6 : 1, cursor: spinning ? 'not-allowed' : 'pointer'
              })}>{spinning ? "Snurrer …" : "Snurr hjulet"}</button>
            )
          )}
        </div>
      </div>

      <div style={{ width: '250px', flex: 'none', alignSelf: 'stretch', paddingTop: '24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '.09em', color: COLOR.textSecondary }}>Igjen i hjulet</span>
          <span style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '20px', color: COLOR.textPrimary }}>{pool.length}</span>
        </div>
        <div style={{ fontSize: '13px', color: COLOR.mutedText }}>Klikk på et navn for å ta det ut av hjulet</div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {pool.map(name => (
            <button key={name} onClick={() => takeOutManually(name)} style={{
              background: COLOR.track, border: '1px solid rgba(255,255,255,.07)', borderRadius: '999px',
              padding: '6px 12px', fontSize: '14px', color: COLOR.chipText, cursor: spinning ? 'default' : 'pointer'
            }}>{name}</button>
          ))}
        </div>

        {out.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {out.map(name => (
              <button key={name} onClick={() => putBack(name)} style={{
                background: 'transparent', border: '1px dashed rgba(255,255,255,.18)', borderRadius: '999px',
                padding: '6px 12px', fontSize: '14px', color: COLOR.textSecondaryOnPanel, cursor: spinning ? 'default' : 'pointer'
              }}>{name} +</button>
            ))}
          </div>
        )}

        {drawn.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {drawn.map(name => (
              <span key={name} style={{
                background: 'transparent', border: '1px solid rgba(255,255,255,.1)', borderRadius: '999px',
                padding: '6px 12px', fontSize: '14px', color: COLOR.mutedText, textDecoration: 'line-through'
              }}>{name}</span>
            ))}
          </div>
        )}

        <button onClick={resetAll} style={{ ...linkBtnStyle(), alignSelf: 'flex-start', fontSize: '14px' }}>Legg alle tilbake</button>
        <div style={{ flex: 1 }} />
      </div>
    </div>
  );
}

// --- Aktivitet: 6-plasser
function SixPlaces({ onStatus }) {
  const [gameState, setGameState] = useState("setup");
  const [rolledNumber, setRolledNumber] = useState(null);
  const [activeNumbers, setActiveNumbers] = useState([1, 2, 3, 4, 5, 6]);
  const [round, setRound] = useState(1);

  useEffect(() => {
    if (onStatus) onStatus(gameState !== "setup" ? { label: "Runde", value: `${round}` } : "");
  }, [gameState, round, onStatus]);

  function startGame() { setGameState("playing"); setRolledNumber(null); setActiveNumbers([1, 2, 3, 4, 5, 6]); setRound(1); }

  function rollDice() {
    if (gameState !== "playing" || activeNumbers.length <= 1) return;
    setGameState("rolling");
    setRolledNumber(null);
    let rollCount = 0;
    const rollInterval = setInterval(() => {
      setRolledNumber(activeNumbers[randInt(0, activeNumbers.length - 1)]);
      rollCount++;
      if (rollCount >= 10) {
        clearInterval(rollInterval);
        const finalNumber = activeNumbers[randInt(0, activeNumbers.length - 1)];
        setRolledNumber(finalNumber);
        const remaining = activeNumbers.filter(n => n !== finalNumber);
        setActiveNumbers(remaining);
        beep(400, 800);
        if (remaining.length <= 1) {
          setTimeout(() => { setGameState("finished"); }, 2500);
        } else {
          setGameState("eliminated");
          setTimeout(() => { setGameState("playing"); setRound(prev => prev + 1); }, 4000);
        }
      }
    }, 150);
  }

  function resetGame() { setGameState("setup"); setRolledNumber(null); setActiveNumbers([1, 2, 3, 4, 5, 6]); setRound(1); }

  if (gameState === "setup") {
    return (
      <IntroScreen
        icon="🎲" name="6-plasser"
        sentence="Terningen bestemmer hvem som er ute — tallet som ruller, er ute resten av spillet."
        badges={[{ icon: '🏃', label: 'Klassen på gulvet' }]}
        primaryLabel="Vis brettet" onPrimary={startGame}
      />
    );
  }

  const winner = gameState === "finished" ? activeNumbers[0] : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '30px', width: '100%', maxWidth: '640px' }}>
      <div style={{
        fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '120px', lineHeight: 1,
        color: winner ? '#16a34a' : COLOR.accentText
      }}>
        {winner ? winner : (rolledNumber || "?")}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', width: '100%' }}>
        {[1, 2, 3, 4, 5, 6].map(number => {
          const isRolled = rolledNumber === number && gameState === "eliminated";
          const isRetired = !activeNumbers.includes(number);
          const isWinner = winner === number;
          let bg = COLOR.card, border = COLOR.shellBorder, color = COLOR.textCard;
          if (isWinner) { bg = 'rgba(22,163,74,.14)'; border = '#16a34a'; color = '#16a34a'; }
          else if (isRolled) { bg = COLOR.accent; border = COLOR.accent; color = COLOR.accentOnDark; }
          else if (isRetired) { bg = 'rgba(239,68,68,.1)'; border = 'rgba(239,68,68,.5)'; color = '#ef4444'; }
          return (
            <div key={number} style={{
              position: 'relative', aspectRatio: '1', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '48px',
              background: bg, border: `1px solid ${border}`, color,
              textDecoration: isRetired ? 'line-through' : 'none'
            }}>{number}</div>
          );
        })}
      </div>
      <div style={{ minHeight: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '19px', color: COLOR.textPrimary, textAlign: 'center' }}>
        {gameState === "eliminated" && rolledNumber ? `Alle på tall ${rolledNumber} er ute! Sett dere ned.`
          : gameState === "finished" && winner ? `Tall ${winner} vant! De som sto der er tilbake i lek.` : ""}
      </div>
      <div style={{ display: 'flex', gap: '12px' }}>
        {gameState === "finished" ? (
          <button onClick={resetGame} style={mainPrimaryBtnStyle()}>Nytt spill</button>
        ) : (
          <>
            <button onClick={rollDice} disabled={gameState !== "playing"} style={mainPrimaryBtnStyle({
              opacity: gameState !== "playing" ? 0.5 : 1, cursor: gameState !== "playing" ? 'not-allowed' : 'pointer'
            })}>
              {gameState === "rolling" ? "Kaster …" : "Kast terning"}
            </button>
            <button onClick={resetGame} style={mainSecondaryBtnStyle()}>Nytt spill</button>
          </>
        )}
      </div>
    </div>
  );
}

// --- Aktivitet: Hangman
function Hangman({ onStatus }) {
  const [started, setStarted] = useState(false);
  const [gameState, setGameState] = useState("setup");
  const [secretWord, setSecretWord] = useState("");
  const [guessedLetters, setGuessedLetters] = useState([]);
  const [wrongGuesses, setWrongGuesses] = useState([]);
  const [inputWord, setInputWord] = useState("");

  const maxWrongGuesses = 6;
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZÆØÅ".split("");

  useEffect(() => {
    if (onStatus) onStatus(gameState !== "setup"
      ? { label: "Feil:", value: `${wrongGuesses.length} av ${maxWrongGuesses}` }
      : "");
  }, [gameState, wrongGuesses, onStatus]);

  function startGame() {
    if (!inputWord.trim()) return;
    const word = inputWord.trim().toUpperCase();
    setSecretWord(word);
    setGuessedLetters([]);
    setWrongGuesses([]);
    setGameState("playing");
    setInputWord("");
  }

  function guessLetter(letter) {
    if (gameState !== "playing" || guessedLetters.includes(letter)) return;
    const newGuessedLetters = [...guessedLetters, letter];
    setGuessedLetters(newGuessedLetters);
    if (secretWord.includes(letter)) {
      const solved = secretWord.split('').every(ch => ch === ' ' || newGuessedLetters.includes(ch));
      if (solved) { setGameState("won"); beep(600, 1200); }
    } else {
      const newWrongGuesses = [...wrongGuesses, letter];
      setWrongGuesses(newWrongGuesses);
      if (newWrongGuesses.length >= maxWrongGuesses) { setGameState("lost"); beep(800, 400); }
    }
  }

  function resetGame() {
    setGameState("setup");
    setSecretWord("");
    setGuessedLetters([]);
    setWrongGuesses([]);
    setInputWord("");
  }

  function getHangmanDrawing() {
    const wrongCount = wrongGuesses.length;
    return (
      <svg width="210" height="260" viewBox="0 0 160 250" style={{ display: 'block' }}>
        <line x1="10" y1="226" x2="90" y2="226" stroke="#4A5260" strokeWidth="6" strokeLinecap="round" />
        <line x1="40" y1="226" x2="40" y2="20" stroke="#4A5260" strokeWidth="6" strokeLinecap="round" />
        <line x1="40" y1="20" x2="130" y2="20" stroke="#4A5260" strokeWidth="6" strokeLinecap="round" />
        <line x1="130" y1="20" x2="130" y2="45" stroke="#4A5260" strokeWidth="6" strokeLinecap="round" />
        {wrongCount >= 1 && <circle cx="130" cy="62" r="16" fill="none" stroke={COLOR.accent} strokeWidth="6" />}
        {wrongCount >= 2 && <line x1="130" y1="78" x2="130" y2="130" stroke={COLOR.accent} strokeWidth="6" strokeLinecap="round" />}
        {wrongCount >= 3 && <line x1="130" y1="92" x2="112" y2="112" stroke={COLOR.accent} strokeWidth="6" strokeLinecap="round" />}
        {wrongCount >= 4 && <line x1="130" y1="92" x2="148" y2="112" stroke={COLOR.accent} strokeWidth="6" strokeLinecap="round" />}
        {wrongCount >= 5 && <line x1="130" y1="130" x2="115" y2="160" stroke={COLOR.accent} strokeWidth="6" strokeLinecap="round" />}
        {wrongCount >= 6 && <line x1="130" y1="130" x2="145" y2="160" stroke={COLOR.accent} strokeWidth="6" strokeLinecap="round" />}
      </svg>
    );
  }

  if (!started) {
    return (
      <IntroScreen
        icon="💀" name="Hangman"
        sentence="En elev skriver inn ordet, resten gjetter én bokstav om gangen."
        badges={[{ icon: '🙋', label: 'Én elev fram', required: true }, { label: '6 feil, så er ordet tapt' }]}
        primaryLabel="Skriv ordet" onPrimary={() => setStarted(true)} showDraw
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '34px', width: '100%', maxWidth: '820px' }}>
      {gameState === "setup" ? (
        <>
          <div style={{ fontSize: '24px', color: COLOR.textSecondaryOnPanel, textAlign: 'center' }}>En elev skriver inn det hemmelige ordet.</div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <input
              type="password"
              value={inputWord}
              onChange={(e) => setInputWord(e.target.value)}
              placeholder="Hemmelig ord …"
              onKeyDown={(e) => e.key === "Enter" && startGame()}
              style={mainFieldStyle({ width: '260px', textAlign: 'center', fontSize: '17px' })}
            />
            <button onClick={startGame} style={mainPrimaryBtnStyle()}>Start spill</button>
          </div>
        </>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '48px', flexWrap: 'wrap', justifyContent: 'center' }}>
            {getHangmanDrawing()}
            <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', justifyContent: 'center' }}>
              {secretWord.split('').map((ch, i) => (
                <div key={i} style={{ width: '62px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '52px', color: COLOR.textPrimary, minHeight: '62px' }}>
                    {ch === ' ' ? '' : (guessedLetters.includes(ch) ? ch : '')}
                  </span>
                  {ch !== ' ' && <div style={{ width: '100%', height: '5px', borderRadius: '99px', background: COLOR.trackLine }} />}
                </div>
              ))}
            </div>
          </div>

          {gameState === "won" && (
            <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '32px', color: COLOR.accentText }}>Dere klarte det!</div>
          )}
          {gameState === "lost" && (
            <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '32px', color: COLOR.accentText }}>Ordet var {secretWord}</div>
          )}

          {gameState === "playing" && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: '8px', width: '620px', maxWidth: '100%' }}>
              {alphabet.map(letter => {
                const isGuessed = guessedLetters.includes(letter);
                const isWrong = wrongGuesses.includes(letter);
                const isCorrect = isGuessed && !isWrong;
                return (
                  <button
                    key={letter}
                    onClick={() => guessLetter(letter)}
                    disabled={isGuessed}
                    style={{
                      aspectRatio: '1', borderRadius: '10px', fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: '21px',
                      cursor: isGuessed ? 'default' : 'pointer',
                      background: isCorrect ? COLOR.accent : isWrong ? 'transparent' : '#1C222C',
                      border: isCorrect ? `1px solid ${COLOR.accent}` : isWrong ? '1px solid rgba(255,255,255,.07)' : '1px solid rgba(255,255,255,.09)',
                      color: isCorrect ? COLOR.accentOnDark : isWrong ? COLOR.mutedText2 : COLOR.textCard
                    }}
                  >{letter}</button>
                );
              })}
            </div>
          )}

          <button onClick={resetGame} style={mainPrimaryBtnStyle()}>Nytt ord</button>
        </>
      )}
    </div>
  );
}

// --- Aktivitet: Dilemma
const DILEMMAS = [
  { left: "🍕 Pizza", right: "🍔 Hamburger" },
  { left: "🍿 Potetgull", right: "🍫 Sjokolade" },
  { left: "📊 Matte", right: "📖 Norsk" },
  { left: "🐕 Hund", right: "🐱 Katt" },
  { left: "☀️ Sommer", right: "❄️ Vinter" },
  { left: "🌅 Morgen", right: "🌙 Kveld" },
  { left: "🎬 Film", right: "📚 Bok" },
  { left: "⚽ Fotball", right: "🏀 Basketball" },
  { left: "🏔️ Fjell", right: "🏖️ Strand" },
   { left: "🍎 Eple", right: "🍌 Banan" },
  { left: "🎮 Spill", right: "🏃‍♀️ Sport" },
  { left: "🍦 Is", right: "🧁 Kake" },
  { left: "🎤 Synge", right: "💃 Danse" },
  { left: "🌧️ Regn", right: "☀️ Sol" },
  { left: "🛏️ Sove lenge", right: "🌅 Stå opp tidlig" },
  { left: "📱 TikTok", right: "📺 YouTube" },
  { left: "🍝 Pasta", right: "🍚 Ris" },
  { left: "🎪 Sirkus", right: "🎭 Teater" },
  { left: "🧩 Puslespill", right: "🎲 Brettspill" },
  { left: "🎸 Gitar", right: "🎹 Piano" },
  { left: "🥤 Brus", right: "🧃 Saft" },
  { left: "🎈 Bursdag", right: "🎄 Jul" },
  { left: "🦄 Enhjørning", right: "🐉 Drage" },
  { left: "🍕 Middag hjemme", right: "🍔 Restaurant" },
  { left: "📖 Lese", right: "✍️ Skrive" },
  { left: "🎢 Berg-og-dal-bane", right: "🎡 Pariserhjul" },
  { left: "🏊‍♀️ Svømming", right: "🚴‍♀️ Sykling" },
  { left: "🎭 Drama", right: "🔬 Naturfag" },
  { left: "🌮 Taco", right: "🍛 Wok" },
  { left: "🎪 Være publikum", right: "🎭 Være på scenen" },
  { left: "🎨 Tegne", right: "🏗️ Bygge" },
  { left: "🌊 Bølger", right: "⛰️ Topper" },
  { left: "🎵 Høre musikk", right: "🎤 Lage musikk" },
  { left: "🍓 Jordbær", right: "🫐 Blåbær" },
  { left: "🚗 Bil", right: "🚲 Sykkel" },
  { left: "🎪 Innendørs", right: "🏕️ Utendørs" },
  { left: "👥 Stor gruppe", right: "👫 Liten gruppe" },

  // Nye morsomme "umulige" dilemmaer
  { left: "🎤 Synge alt du sier", right: "🕺 Danse hver gang du beveger deg" },
  { left: "🍕 Spise bare pizza resten av livet", right: "🚫 Aldri spise pizza igjen" },
  { left: "📱 Mobilen spiller Bieber høyt ved hver melding", right: "🤳 Mobilen poster bilde hver gang du gjesper" },
  { left: "📢 Presentasjon foran hele skolen hver fredag", right: "⏰ Aldri friminutt lenger enn 5 minutter" },
  { left: "👩‍🏫 Læreren leser alle mobilmeldingene dine høyt", right: "👨‍👩‍👧 Foreldrene følger deg til skolen hver dag og sier «ha en fin dag, vennen»" },
  { left: "⏰ Skole fra 07:00–14:00", right: "🌙 Skole fra 12:00–19:00" },
  { left: "🧠 Kunne lese andres tanker", right: "🤥 Aldri kunne lyve igjen" },
  { left: "🍦 All mat smaker som is", right: "🥦 All mat smaker som brokkoli" },
  { left: "📺 Bare se samme film om og om igjen", right: "🎵 Bare høre samme sang om og om igjen" },
  { left: "📚 Gjøre lekser hver dag i sommerferien", right: "🏫 Gå på skolen annenhver lørdag" },
  { left: "🌍 Leve uten internett", right: "📞 Leve uten mobil" },
  { left: "🦸 Alltid være usynlig", right: "⚡ Alltid være supersynlig (alle legger merke til deg)" },
  { left: "👟 Alltid gå med to forskjellige sko", right: "👕 Alltid gå med klær bak-frem" },
  { left: "🛏️ Aldri være trøtt", right: "🍽️ Aldri være sulten" },
  { left: "🐵 Høres ut som en ape når du ler", right: "🦆 Høres ut som en and når du snakker" }
];

function Dilemma() {
  const [state, setState] = useAppState();
  const [currentDilemma, setCurrentDilemma] = useState(null);
  const [usedDilemmas, setUsedDilemmas] = useState([]);
  const [started, setStarted] = useState(false);
  const theme = state.theme === "mørkt" ? "mørkt" : "lyst";

  function pickDilemma() {
    const availableDilemmas = usedDilemmas.length >= DILEMMAS.length
      ? DILEMMAS
      : DILEMMAS.filter(d => !usedDilemmas.find(used => used.left === d.left && used.right === d.right));
    const dilemma = availableDilemmas[randInt(0, availableDilemmas.length - 1)];
    setCurrentDilemma(dilemma);
    setUsedDilemmas(usedDilemmas.length >= DILEMMAS.length ? [dilemma] : [...usedDilemmas, dilemma]);
  }

  useEffect(() => { pickDilemma(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function drawExplainer() {
    const active = state.classes.find((c) => c.id === state.activeClassId) || state.classes[0];
    const draw = state.draw;
    const source = draw.mode === "team" ? (active?.teams || []).map(t => t.name) : (active?.students || []);
    if (!source.length) return;
    const remaining = draw.pool.length ? draw.pool : source;
    const pick = remaining[randInt(0, remaining.length - 1)];
    const idx = remaining.indexOf(pick);
    const poolAfter = [...remaining.slice(0, idx), ...remaining.slice(idx + 1)];
    setState({ ...state, draw: { ...draw, pool: poolAfter, current: pick, total: draw.pool.length ? draw.total : source.length } });
    beep(150, 1400);
  }

  const colors = theme === "mørkt"
    ? { leftBg: '#2A2113', leftText: '#F7ECD9', leftMeta: '#C9B189', rightBg: '#19203A', rightText: '#EAEFFF', rightMeta: '#A9B6E0', stepBg: '#151A22', stepEdge: 'rgba(255,255,255,.08)', stepText: '#D5DAE3', stepNum: COLOR.accent }
    : { leftBg: '#FFE9C2', leftText: '#4A3208', leftMeta: '#6E5220', rightBg: '#D8E2FF', rightText: '#1B2A5C', rightMeta: '#374878', stepBg: '#FFFFFF', stepEdge: 'rgba(0,0,0,.1)', stepText: '#3E4653', stepNum: '#8F4506' };

  const steps = ["Alle går til den siden de velger", "Tell opp hvor mange som står hvor", "Én fra hver side forklarer valget"];

  if (!started) {
    return (
      <IntroScreen
        icon="⚖️" name="Dilemma"
        sentence="Klassen deler seg i to sider av rommet og forklarer valget sitt."
        badges={[{ icon: '🏃', label: 'Klassen på gulvet' }]}
        primaryLabel="Vis dilemma" onPrimary={() => setStarted(true)}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '30px', width: '100%', maxWidth: '1000px' }}>
      <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '44px', letterSpacing: '-.015em', color: COLOR.textPrimary }}>Hva velger du?</div>

      <div aria-live="polite" style={{ position: 'relative', display: 'flex', gap: '20px', width: '100%' }}>
        <div style={{
          flex: 1, minHeight: '270px', borderRadius: '22px', padding: '42px 34px', background: colors.leftBg,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '18px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '17px', fontWeight: 600, letterSpacing: '.06em', color: colors.leftMeta }}>
            <span aria-hidden="true">⬅</span>VENSTRE SIDE
          </div>
          <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 'clamp(34px, 4vw, 52px)', lineHeight: 1.06, letterSpacing: '-.02em', textAlign: 'center', textWrap: 'balance', color: colors.leftText }}>
            {currentDilemma?.left}
          </div>
        </div>
        <div style={{
          flex: 1, minHeight: '270px', borderRadius: '22px', padding: '42px 34px', background: colors.rightBg,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '18px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '17px', fontWeight: 600, letterSpacing: '.06em', color: colors.rightMeta }}>
            HØYRE SIDE<span aria-hidden="true">➡</span>
          </div>
          <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 'clamp(34px, 4vw, 52px)', lineHeight: 1.06, letterSpacing: '-.02em', textAlign: 'center', textWrap: 'balance', color: colors.rightText }}>
            {currentDilemma?.right}
          </div>
        </div>
        <div style={{
          position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)',
          width: '72px', height: '72px', borderRadius: '50%', background: COLOR.surfaceBg, border: `1px solid ${COLOR.shellBorder}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '14px', fontWeight: 600, letterSpacing: '.06em', color: COLOR.textSecondary
        }}>ELLER</div>
      </div>

      <div style={{ display: 'flex', gap: '14px', width: '100%' }}>
        {steps.map((s, i) => (
          <div key={i} style={{
            flex: 1, borderRadius: '14px', padding: '16px 18px', display: 'flex', alignItems: 'center', gap: '14px',
            border: `1px solid ${colors.stepEdge}`, background: colors.stepBg
          }}>
            <span style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '26px', color: colors.stepNum }}>{i + 1}</span>
            <span style={{ fontSize: '19px', lineHeight: 1.3, color: colors.stepText }}>{s}</span>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '12px' }}>
        <button onClick={pickDilemma} style={mainPrimaryBtnStyle()}>Nytt dilemma</button>
        <button onClick={drawExplainer} style={mainSecondaryBtnStyle()}>Trekk en som forklarer</button>
      </div>
    </div>
  );
}

// --- Aktivitet: Fire lys
function FourLights({ onStatus }) {
  const [gameState, setGameState] = useState("waiting");
  const [currentRound, setCurrentRound] = useState(1);
  const [activeLight, setActiveLight] = useState(null);
  const [lightPositions, setLightPositions] = useState([1, 2, 3, 4]);
  const [justShuffled, setJustShuffled] = useState(false);

  useEffect(() => {
    if (onStatus) onStatus({ label: "Runde", value: `${currentRound}` });
  }, [currentRound, onStatus]);

  const baseLights = [
    { id: 1, emoji: "💨", instruction: "Ta ett skritt frem", bright: '#dc2626', glow: 'rgba(220,38,38,.5)', textOnBright: '#ffffff' },
    { id: 2, emoji: "⬅️", instruction: "Ta ett skritt tilbake", bright: '#16a34a', glow: 'rgba(22,163,74,.5)', textOnBright: '#ffffff' },
    { id: 3, emoji: "🚫", instruction: "Ikke gjør noe", bright: '#eab308', glow: 'rgba(234,179,8,.5)', textOnBright: '#0a0c10' },
    { id: 4, emoji: "🦆", instruction: "Sett deg på huk", bright: '#7c3aed', glow: 'rgba(124,58,237,.5)', textOnBright: '#ffffff' }
  ];
  const GRAY_BG = '#1C222C';
  const GRAY_BORDER = 'rgba(255,255,255,.09)';
  const GRAY_TEXT = 'rgba(255,255,255,.4)';

  const lights = lightPositions.map(pos => baseLights[pos - 1]);

  function startRound() {
    if (gameState !== "waiting") return;
    setGameState("starting");
    setActiveLight(null);
    const willShuffle = currentRound >= 11;
    if (willShuffle) setLightPositions(shuffle([1, 2, 3, 4]));
    setJustShuffled(willShuffle);
    setTimeout(() => {
      setGameState("ready");
      const delay = Math.random() * 8000 + 4000;
      setTimeout(() => {
        const randomLight = Math.floor(Math.random() * 4) + 1;
        setActiveLight(randomLight);
        setGameState("active");
        beep(300, 1200);
        setTimeout(() => { setGameState("finished"); }, 3000);
      }, delay);
    }, 1500);
  }

  function nextRound() {
    if (gameState !== "finished") return;
    setCurrentRound(prev => prev + 1);
    setGameState("waiting");
    setActiveLight(null);
    setJustShuffled(false);
  }

  function resetGame() {
    setGameState("waiting");
    setCurrentRound(1);
    setActiveLight(null);
    setLightPositions([1, 2, 3, 4]);
    setJustShuffled(false);
  }

  const showInstructions = currentRound <= 3;
  const showEmojis = currentRound <= 6;
  const activeLightMeta = baseLights.find(l => l.id === activeLight);

  let message = "";
  if (gameState === "waiting") message = "Trykk «Start runde» for å begynne.";
  else if (gameState === "starting") message = justShuffled ? "Lysene har byttet plass! Se nøye etter …" : "Runden starter …";
  else if (gameState === "ready") message = justShuffled ? "Lysene byttet plass — vær klar! Lyset kan komme når som helst." : "Vær klar … lyset kan komme når som helst!";
  else if (gameState === "finished" && activeLightMeta) message = `Riktig var «${activeLightMeta.instruction}». Eliminer de som ikke gjorde dette, eller som var sist.`;
  const isShuffleNotice = message.includes('byttet plass');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '18px', width: '100%', maxWidth: '640px' }}>
      <div style={{
        minHeight: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center',
        fontSize: '18px', lineHeight: 1.3, color: isShuffleNotice ? COLOR.accent : COLOR.textSecondary,
        fontWeight: isShuffleNotice ? 600 : 400
      }}>{message}</div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 270px)', gridTemplateRows: 'repeat(2, 206px)', gap: '18px' }}>
        {lights.map((light) => {
          const isChosen = (gameState === "active" || gameState === "finished") && activeLight === light.id;
          const isOn = gameState === "waiting" || isChosen;
          return (
            <div key={light.id} style={{
              borderRadius: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px',
              background: isOn ? light.bright : GRAY_BG,
              border: `1px solid ${isOn ? light.bright : GRAY_BORDER}`,
              color: isOn ? light.textOnBright : GRAY_TEXT,
              boxShadow: isChosen ? `0 0 40px ${light.glow}` : 'none',
              transition: 'background 0.25s ease, box-shadow 0.25s ease, color 0.25s ease'
            }}>
              {showEmojis && <div style={{ fontSize: isChosen ? '64px' : '44px' }}>{light.emoji}</div>}
              {showInstructions && (
                <div style={{ fontSize: '26px', fontWeight: 700, fontFamily: FONT_DISPLAY, textAlign: 'center', padding: '0 16px' }}>
                  {light.instruction}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {gameState === "active" && activeLightMeta && (
        <div style={{
          fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '36px',
          color: activeLightMeta.bright, textAlign: 'center'
        }}>{activeLightMeta.instruction}!</div>
      )}

      <div style={{ display: 'flex', gap: '12px' }}>
        {gameState === "finished" ? (
          <button onClick={nextRound} style={mainPrimaryBtnStyle()}>Neste runde</button>
        ) : (
          <button onClick={startRound} disabled={gameState !== "waiting"} style={mainPrimaryBtnStyle({
            opacity: gameState !== "waiting" ? 0.5 : 1, cursor: gameState !== "waiting" ? 'not-allowed' : 'pointer'
          })}>Start runde</button>
        )}
        <button onClick={resetGame} style={mainSecondaryBtnSmallStyle()}>Nullstill spill</button>
      </div>
    </div>
  );
}

// --- Aktivitet: Tidslinje (Forbedret versjon)
const HISTORICAL_EVENTS = [
  // Verdenshistorie - antikken og middelalder
  { event: "Pyramidene i Egypt bygges", year: -2500, category: "Antikken", fact: "Den største pyramiden, Keopspyramiden, besto av ca. 2,3 millioner steinblokker." },
  { event: "Kristi fødsel", year: 0, category: "Antikken", fact: "Vår moderne kalender er basert på dette årstallet, selv om historikere mener Jesus ble født noen år tidligere." },
  { event: "Det vestromerske riket faller", year: 476, category: "Antikken", fact: "Dette regnes ofte som starten på middelalderen i Vest-Europa." },
  { event: "Vikingtiden begynner", year: 793, category: "Middelalder", fact: "Angrepet på klosteret i Lindisfarne, England, markerer tradisjonelt starten på vikingtiden." },
  { event: "Første korstog", year: 1095, category: "Middelalder", fact: "Etter en oppfordring fra Pave Urban II, dro europeiske riddere til Midtøsten for å erobre Jerusalem." },
  { event: "Svartedauden kom til Norge", year: 1349, category: "Middelalder", fact: "Pesten utslettet anslagsvis en tredjedel av Europas befolkning." },
  
  // Norge - middelalder og tidlig nyere tid
  { event: "Harald Hårfagre samler Norge", year: 872, category: "Norsk historie", fact: "Slaget i Hafrsfjord regnes som den avgjørende hendelsen for Norges samling til ett rike." },
  { event: "Kristendommen innføres i Norge", year: 1000, category: "Norsk historie", fact: "Selv om Olav Tryggvason startet kristningen, var det Olav den Hellige som fullførte den." },
  { event: "Slaget på Stiklestad", year: 1030, category: "Norsk historie", fact: "Olav den Hellige falt i dette slaget, noe som fikk stor betydning for kristningen av Norge." },
  { event: "Den norske union med Danmark begynner", year: 1380, category: "Norsk historie", fact: "Unionen, ofte kalt 400-årsnatten, varte helt til 1814." },
  { event: "Norge får egen grunnlov på Eidsvoll", year: 1814, category: "Norsk historie", fact: "Grunnloven ble signert 17. mai, som nå er Norges nasjonaldag." },
  { event: "Union med Sverige oppløses", year: 1905, category: "Norsk historie", fact: "Oppløsningen skjedde fredelig etter en folkeavstemning der nesten 100% stemte for." },
  
  // Oppdagelsesreiser og renessanse
  { event: "Columbus 'oppdager' Amerika", year: 1492, category: "Oppdagelser", fact: "Columbus trodde han hadde nådd India, og kalte derfor urbefolkningen for 'indianere'." },
  { event: "Gutenberg oppfinner boktrykkerkunsten", year: 1440, category: "Oppfinnelser", fact: "Dette gjorde det mulig å masseprodusere bøker og spre kunnskap raskere enn noensinne." },
  { event: "Leonardo da Vinci maler Mona Lisa", year: 1503, category: "Kunst", fact: "Maleriet er kjent for sitt mystiske smil og henger i Louvre-museet i Paris." },
  { event: "Magellan starter første verdensomseiling", year: 1519, category: "Oppdagelser", fact: "Magellan selv døde på Filippinene, men ett av hans skip fullførte reisen." },
  
  // 1600-1700-tallet
  { event: "Shakespeares død", year: 1616, category: "Litteratur", fact: "Han skrev minst 37 skuespill, inkludert klassikere som 'Hamlet' og 'Romeo og Julie'." },
  { event: "Isaac Newton publiserer 'Principia'", year: 1687, category: "Vitenskap", fact: "Boken la grunnlaget for klassisk mekanikk og formulerte lovene om tyngdekraft." },
  { event: "Den franske revolusjon begynner", year: 1789, category: "Politikk", fact: "Stormingen av Bastillen 14. juli er en sentral hendelse som markerer starten på revolusjonen." },
  
  // 1800-tallet
  { event: "Napoleon tapte ved Waterloo", year: 1815, category: "Krig", fact: "Nederlaget markerte slutten på Napoleonskrigene og hans keiserdømme." },
  { event: "Den første jernbanen åpnes", year: 1825, category: "Teknologi", fact: "Verdens første offentlige jernbane gikk mellom Stockton og Darlington i England." },
  { event: "Darwin publiserer 'Om artenes opprinnelse'", year: 1859, category: "Vitenskap", fact: "Boken presenterte teorien om evolusjon gjennom naturlig utvalg." },
  { event: "Alexander Graham Bell oppfinner telefonen", year: 1876, category: "Oppfinnelser", fact: "De første ordene som ble sagt i en telefon var 'Mr. Watson, come here, I want to see you'." },
  { event: "Edison oppfinner lyspæra", year: 1879, category: "Oppfinnelser", fact: "Thomas Edisons versjon var den første som var praktisk og langvarig nok for kommersiell bruk." },
  { event: "Eiffeltårnet bygges", year: 1889, category: "Arkitektur", fact: "Det ble bygget for Verdensutstillingen i Paris og var ment å være en midlertidig struktur." },
  
  // Tidlig 1900-tall
  { event: "Første verdenskrig begynner", year: 1914, category: "Krig", fact: "Krigen startet etter skuddene i Sarajevo, der erkehertug Franz Ferdinand ble myrdet." },
  { event: "Roald Amundsen når Sydpolen", year: 1911, category: "Oppdagelser", fact: "Han og hans team var de første menneskene som nådde Sydpolen, og de slo en britisk ekspedisjon." },
  { event: "Titanic synker", year: 1912, category: "Katastrofer", fact: "Skipet ble ansett som 'usynkelig' før det traff et isfjell på jomfruturen." },
  { event: "Brødrene Wright flyr første gang", year: 1903, category: "Teknologi", fact: "Den første vellykkede flyturen varte bare 12 sekunder og fløy 37 meter." },
  { event: "Første verdenskrig slutter", year: 1918, category: "Krig", fact: "Krigen endte med signeringen av våpenhvileavtalen klokken 11 den 11. november." },
  { event: "Kvinner får stemmerett i Norge", year: 1913, category: "Norsk historie", fact: "Norge var et av de første landene i verden som innførte allmenn stemmerett for kvinner." },
  
  // Mellomkrigstiden
  { event: "Alexander Fleming oppdager penicillin", year: 1928, category: "Medisin", fact: "Oppdagelsen skjedde ved en tilfeldighet da en bakteriekultur ble forurenset av muggsopp." },
  { event: "Børskrakket på Wall Street", year: 1929, category: "Økonomi", fact: "Krakket utløste 'Den store depresjonen', en verdensomspennende økonomisk krise." },
  { event: "Hitler kommer til makten i Tyskland", year: 1933, category: "Politikk", fact: "Han ble utnevnt til rikskansler, noe som markerte begynnelsen på Nazi-Tysklands regime." },
  
  // Andre verdenskrig
  { event: "Andre verdenskrig begynner", year: 1939, category: "Krig", fact: "Krigen startet da Tyskland invaderte Polen 1. september." },
  { event: "Tyskland invaderer Norge", year: 1940, category: "Norsk historie", fact: "Angrepet, kalt 'Operasjon Weserübung', skjedde 9. april." },
  { event: "Japan angriper Pearl Harbor", year: 1941, category: "Krig", fact: "Angrepet førte til at USA gikk med i andre verdenskrig." },
  { event: "D-dagen: Normandie-landgangen", year: 1944, category: "Krig", fact: "Den største amfibiske invasjonen i historien, som startet frigjøringen av Vest-Europa." },
  { event: "Atombomber slippes over Japan", year: 1945, category: "Krig", fact: "Bomben over Hiroshima var den første gangen et atomvåpen ble brukt i krig." },
  { event: "Andre verdenskrig slutter", year: 1945, category: "Krig", fact: "Tyskland kapitulerte i mai, og Japan kapitulerte i september etter atombombene." },
  
  // Kald krig-perioden
  { event: "FN etableres", year: 1945, category: "Politikk", fact: "Organisasjonen ble opprettet for å forhindre fremtidige verdenskriger." },
  { event: "Israel etableres som stat", year: 1948, category: "Politikk", fact: "Etableringen førte umiddelbart til krig med nabolandene." },
  { event: "NATO etableres", year: 1949, category: "Politikk", fact: "En forsvarsallianse mellom USA, Canada og vesteuropeiske land som svar på Sovjetunionens makt." },
  { event: "Korean-krigen begynner", year: 1950, category: "Krig", fact: "En konflikt mellom Nord-Korea (støttet av Kina og Sovjet) og Sør-Korea (støttet av FN/USA)." },
  { event: "DNA-strukturen oppdages", year: 1953, category: "Vitenskap", fact: "James Watson og Francis Crick publiserte sin modell av dobbeltheliks-strukturen." },
  { event: "Sputnik sendes opp", year: 1957, category: "Romfart", fact: "Sovjetunionens oppskytning av den første satellitten startet 'romkappløpet' med USA." },
  { event: "Berlinmuren bygges", year: 1961, category: "Politikk", fact: "Muren ble bygget for å stoppe folk fra å flykte fra Øst- til Vest-Berlin." },
  { event: "John F. Kennedy skutt", year: 1963, category: "Politikk", fact: "Attentatet i Dallas, Texas, sjokkerte verden og er fortsatt gjenstand for konspirasjonsteorier." },
  { event: "Martin Luther King holder 'I have a dream'-talen", year: 1963, category: "Borgerrettigheter", fact: "Talen ble holdt under 'Marsjen mot Washington' og er et ikonisk øyeblikk i borgerrettighetskampen." },
  { event: "Neil Armstrong går på månen", year: 1969, category: "Romfart", fact: "Hans berømte ord var: 'That's one small step for [a] man, one giant leap for mankind'." },
  { event: "Woodstock-festivalen", year: 1969, category: "Kultur", fact: "En ikonisk musikkfestival som samlet over 400 000 mennesker og definerte en generasjon." },
  
  // 1970-80-tallet
  { event: "Vietnam-krigen slutter", year: 1975, category: "Krig", fact: "Krigen endte med at Nord-Vietnam erobret Sør-Vietnam, og landet ble gjenforent under kommunistisk styre." },
  { event: "Apple lanserer første datamaskin", year: 1976, category: "Teknologi", fact: "Apple I var en av de første personlige datamaskinene, og ble solgt som et byggesett." },
  { event: "Tsjernobyl-ulykken", year: 1986, category: "Katastrofer", fact: "Den verste atomkraftulykken i historien, med radioaktivt nedfall over store deler av Europa." },
  { event: "Berlinmuren faller", year: 1989, category: "Politikk", fact: "Murens fall symboliserte slutten på den kalde krigen og førte til Tysklands gjenforening." },
  
  // 1990-tallet
  { event: "Sovjetunionen oppløses", year: 1991, category: "Politikk", fact: "Oppløsningen markerte den endelige slutten på den kalde krigen." },
  { event: "Internett (www) oppfinnes", year: 1991, category: "Teknologi", fact: "Oppfunnet av Tim Berners-Lee ved CERN for å hjelpe forskere med å dele informasjon." },
  { event: "Nelson Mandela blir president i Sør-Afrika", year: 1994, category: "Politikk", fact: "Dette markerte slutten på apartheid-systemet etter at Mandela hadde sittet 27 år i fengsel." },
  { event: "Den andre norske EU-avstemningen", year: 1994, category: "Norsk historie", fact: "Resultatet var et knapt nei-flertall, med '52,2%' som stemte mot norsk medlemskap og '47,8%' for" },
  { event: "Prinsesse Diana dør", year: 1997, category: "Berømtheter", fact: "Hennes død i en bilulykke i Paris utløste en verdensomspennende sorgreaksjon." },
  
  // 2000-tallet
  { event: "Terrorangrepet 11. september", year: 2001, category: "Terrorisme", fact: "Fire passasjerfly ble kapret; to traff World Trade Center i New York." },
  { event: "iPhone lanseres", year: 2007, category: "Teknologi", fact: "Den første iPhonen revolusjonerte mobiltelefonmarkedet med sin store berøringsskjerm." },
  { event: "Barack Obama blir USAs president", year: 2009, category: "Politikk", fact: "Han var den første afroamerikanske presidenten i USAs historie." },
  
  // 2010-tallet
  { event: "Facebook når 1 milliard brukere", year: 2012, category: "Teknologi", fact: "En milepæl for sosiale medier, som viste hvor integrert det hadde blitt i folks liv." },
  { event: "Donald Trump blir USAs president første gang", year: 2017, category: "Politikk", fact: "Hans valgseier ble ansett som en stor overraskelse i politiske kretser." },
  { event: "Brexit-avstemningen", year: 2016, category: "Politikk", fact: "Storbritannia stemte for å forlate Den europeiske union (EU)." },
  
  // 2020-tallet
  { event: "COVID-19 pandemien begynner", year: 2020, category: "Helse", fact: "Verdens helseorganisasjon (WHO) erklærte utbruddet som en global pandemi 11. mars 2020." },
  { event: "Joe Biden blir USAs president", year: 2021, category: "Politikk", fact: "Han ble den eldste personen som noensinne er innsatt som amerikansk president." },
  
  // Norske spesielle hendelser
  { event: "Olje oppdages i Nordsjøen", year: 1969, category: "Norsk historie", fact: "Funnet av Ekofisk-feltet forvandlet Norge til en av verdens rikeste nasjoner." },
  { event: "OL i Lillehammer", year: 1994, category: "Norsk historie", fact: "Vinter-OL i 1994 regnes som et av de mest vellykkede i historien." },
  { event: "22. juli-angrepene", year: 2011, category: "Norsk historie", fact: "Terrorangrepene i Oslo og på Utøya er den verste voldshandlingen i Norge siden andre verdenskrig." }
];

const TIMELINE_COUNT_OPTIONS = [{ label: '3 hendelser', value: 3 }, { label: '4 hendelser', value: 4 }, { label: '5 hendelser', value: 5 }];

function Timeline({ onStatus }) {
  const [gameState, setGameState] = useState("setup");
  const [numEvents, setNumEvents] = useState(4);
  const [events, setEvents] = useState([]);
  const [userOrder, setUserOrder] = useState([]);
  const [usedEventSets, setUsedEventSets] = useState([]);
  const [isCorrect, setIsCorrect] = useState(false);
  const setupRef = useRef(null);

  useEffect(() => {
    if (gameState === 'setup' && setupRef.current) {
      setupRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [gameState]);

  useEffect(() => {
    if (onStatus) onStatus(gameState === 'finished' ? "fasit vist" : (gameState === 'playing' ? "sorterer" : ""));
  }, [gameState, onStatus]);

  function generateEventSet(numEvents) {
    const availableEvents = HISTORICAL_EVENTS.filter(event =>
      !usedEventSets.some(usedSet => usedSet.some(usedEvent => usedEvent.event === event.event))
    );
    if (availableEvents.length < numEvents) {
      setUsedEventSets([]);
      return shuffle(HISTORICAL_EVENTS).slice(0, numEvents);
    }
    const selectedEvents = shuffle(availableEvents).slice(0, numEvents);
    setUsedEventSets(prev => [...prev, selectedEvents]);
    return selectedEvents;
  }

  function startGame(numEvents) {
    const selectedEvents = generateEventSet(numEvents);
    setEvents(shuffle(selectedEvents));
    setUserOrder([]);
    setIsCorrect(false);
    setGameState("playing");
  }

  function handleEventClick(event) {
    if (gameState !== "playing") return;
    if (userOrder.includes(event)) {
      setUserOrder(userOrder.filter(e => e !== event));
    } else {
      setUserOrder([...userOrder, event]);
    }
  }

  function checkAnswer() {
    const correctOrder = [...events].sort((a, b) => a.year - b.year);
    const isAnswerCorrect = userOrder.length === events.length &&
      userOrder.every((event, index) => {
        const correctYear = correctOrder[index].year;
        const eventsWithSameYear = events.filter(e => e.year === correctYear);
        return eventsWithSameYear.includes(event);
      });
    setIsCorrect(isAnswerCorrect);
    setGameState("finished");
    if (isAnswerCorrect) { beep(600, 1200); } else { beep(400, 800); }
  }

  function resetGame() {
    setGameState("setup");
    setEvents([]);
    setUserOrder([]);
  }

  if (gameState === "setup") {
    return (
      <div ref={setupRef} style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <IntroScreen
          icon="📅" name="Tidslinje"
          sentence="Sett hendelsene i riktig rekkefølge, fra eldst til nyest."
          toggle={{ value: numEvents, onChange: setNumEvents, options: TIMELINE_COUNT_OPTIONS, label: "Antall hendelser" }}
          primaryLabel="Vis hendelsene" onPrimary={() => startGame(numEvents)}
        />
      </div>
    );
  }

  return (
    <div style={{ width: '100%', maxWidth: '1100px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 40px 0' }}>
        <Bryter
          value={numEvents}
          onChange={(n) => { setNumEvents(n); startGame(n); }}
          options={TIMELINE_COUNT_OPTIONS} label="Antall hendelser" small
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '28px', padding: '8px 40px 10px' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '.09em', color: COLOR.textSecondary }}>Ikke plassert</div>
          {events.filter(e => !userOrder.includes(e)).map(event => (
            <button key={event.event} onClick={() => handleEventClick(event)} style={{
              background: COLOR.card, border: `1px solid ${COLOR.shellBorder}`, borderRadius: '14px',
              padding: '20px 22px', fontSize: '24px', fontWeight: 700, fontFamily: FONT_DISPLAY,
              lineHeight: 1.25, color: COLOR.textCard, textAlign: 'left', cursor: 'pointer'
            }}
            onMouseOver={(e) => { e.currentTarget.style.background = COLOR.cardHover; e.currentTarget.style.borderColor = COLOR.accent; }}
            onMouseOut={(e) => { e.currentTarget.style.background = COLOR.card; e.currentTarget.style.borderColor = COLOR.shellBorder; }}
            >{event.event}</button>
          ))}
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '.09em', color: COLOR.textSecondary }}>
            Klassens rekkefølge{gameState === 'finished' ? ' · Fasit' : ''}
          </div>
          {userOrder.length > 0 ? userOrder.map((event, index) => (
            <button key={event.event} onClick={() => handleEventClick(event)} style={{
              background: COLOR.card, border: `1px solid ${COLOR.shellBorder}`, borderRadius: '14px',
              padding: '16px 22px', display: 'flex', gap: '16px', alignItems: 'flex-start',
              textAlign: 'left', cursor: 'pointer'
            }}>
              <span style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '26px', color: COLOR.accentText }}>{index + 1}</span>
              <div>
                <div style={{ fontSize: '24px', fontWeight: 700, fontFamily: FONT_DISPLAY, color: COLOR.textCard }}>{event.event}</div>
                {gameState === 'finished' && (
                  <div style={{ fontSize: '17px', color: COLOR.accentText, marginTop: '4px' }}>
                    {event.year < 0 ? `${Math.abs(event.year)} f.Kr.` : `År ${event.year}`}
                  </div>
                )}
              </div>
            </button>
          )) : (
            <div style={{
              color: COLOR.mutedText, fontSize: '15px', padding: '30px 20px', border: '2px dashed rgba(255,255,255,.12)',
              borderRadius: '14px', textAlign: 'center'
            }}>Klikk på hendelser til venstre.</div>
          )}
        </div>
      </div>

      <div style={{ textAlign: 'center', padding: '0 40px 40px' }}>
        {gameState === "playing" && (
          <button onClick={checkAnswer} disabled={userOrder.length !== events.length} style={mainPrimaryBtnStyle({
            opacity: userOrder.length === events.length ? 1 : 0.5,
            cursor: userOrder.length === events.length ? 'pointer' : 'not-allowed'
          })}>Sjekk svar</button>
        )}
        {gameState === "finished" && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
            <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '28px', color: COLOR.accentText }}>
              {isCorrect ? "Helt riktig!" : "Ikke helt riktig — se fasiten over."}
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => startGame(events.length)} style={mainPrimaryBtnStyle()}>Nye hendelser</button>
              <button onClick={resetGame} style={mainSecondaryBtnStyle()}>Start på nytt</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// --- Aktivitet: Fakta eller Myte
const FACT_MYTH_STATEMENTS = [
  // Sanne påstander
  { statement: "Haier er faktisk fisk, ikke sjødyr", isTrue: true, explanation: "Haier tilhører klassen bruskfisk og er dermed fisk, selv om mange tror de er sjødyr." },
  { statement: "Bananer er teknisk sett bær", isTrue: true, explanation: "Botanisk sett er bananer klassifisert som bær, mens jordbær ikke er det." },
  { statement: "Honning kan aldri bli dårlig", isTrue: true, explanation: "Arkeologer har funnet spiselig honning i egyptiske graver som er over 3000 år gamle." },
    { statement: "Blekkspruten har tre hjerter", isTrue: true, explanation: "To hjerter pumper blod til gjellene, mens det tredje pumper blod til resten av kroppen." },
  { statement: "Det er flere mulige sjakkpartier enn atomer i det observerbare universet", isTrue: true, explanation: "Antallet mulige sjakkpartier er så enormt stort at det overgår antallet atomer i universet." },
  { statement: "Krokodiller kan ikke stikke tunga ut", isTrue: true, explanation: "Krokodiller har tunga fast til gummen, så de kan ikke strekke den ut." },
  { statement: "Norge har flere øyer enn Finland", isTrue: true, explanation: "Norge har rundt 239 000 øyer, mens Finland har ca. 179 000 øyer. (Dette kan variere avhengig av definisjon)" },
  { statement: "En gruppe ugler kalles et 'parliament'", isTrue: true, explanation: "På engelsk kalles en flokk ugler for et 'parliament', muligens på grunn av deres kloke utseende." },
  { statement: "Wombat-avføring er firkantet", isTrue: true, explanation: "Wombater er de eneste dyrene som produserer firkantet avføring, takket være deres unike tarmsystem." },
  
  // Falske påstander
  { statement: "Mennesker bruker kun 10% av hjernen sin", isTrue: false, explanation: "Dette er en myte. Vi bruker nesten hele hjernen vår, selv under enkle oppgaver." },
  { statement: "Lynet slår aldri ned på samme sted to ganger", isTrue: false, explanation: "Lynet kan absolutt slå ned på samme sted flere ganger. Empire State Building treffes ca. 100 ganger i året." },
  { statement: "Kameleoner skifter farge for å kamuflere seg", isTrue: false, explanation: "Kameleoner skifter farge hovedsakelig for å kommunisere følelser og regulere temperatur, ikke kamuflasje." },
    { statement: "Gullfisk har kun 3 sekunders hukommelse", isTrue: false, explanation: "Gullfisk kan faktisk huske ting i måneder, ikke bare sekunder." },
  { statement: "Vi har kun fem sanser", isTrue: false, explanation: "Mennesker har mange flere sanser, inkludert balanse, temperatur, smerte, og kroppens posisjon i rommet." },
  { statement: "Hår og negler fortsetter å vokse etter døden", isTrue: false, explanation: "Dette er en illusjon som oppstår fordi huden trekker seg sammen etter døden, slik at hår og negler ser lengre ut." },
  { statement: "Lyn slår alltid oppover fra bakken", isTrue: false, explanation: "Lyn kan bevege seg i begge retninger - både fra sky til bakke og fra bakke til sky." },
  { statement: "Man kan se den kinesiske mur fra månen", isTrue: false, explanation: "Dette er en gjenganger myte. Den kinesiske mur er ikke synlig fra månen uten tekniske hjelpemidler." },
  { statement: "Alle snøflak er unike", isTrue: false, explanation: "Selv om sjansen er liten, er det teoretisk mulig at to snøflak kan være identiske." },
  
  // Norske/nordiske påstander
  { statement: "Norge har verdens lengste kystlinje", isTrue: false, explanation: "Canada har faktisk verdens lengste kystlinje, ikke Norge." },
  { statement: "Det norske ordet 'ski' kommer fra det gamle norrøne ordet 'skíð'", isTrue: true, explanation: "Ordet ski kommer faktisk fra gammelnorsk og betyr 'spaltet ved' eller 'trestykke'." },
  { statement: "Vikingene hadde horn på hjelmene sine", isTrue: false, explanation: "Dette er en moderne myte. Arkeologiske funn viser at vikinghjelmer ikke hadde horn." },
  { statement: "Norge oppfant ostehøvelen", isTrue: true, explanation: "Thor Bjørklund oppfant ostehøvelen i 1925." },
  { statement: "Island har flest aktive vulkaner i Europa", isTrue: true, explanation: "Island ligger på den mid-atlantiske rygg og har over 30 aktive vulkaner." },
  
  // Vitenskapelige påstander
  { statement: "Diamant er det hardeste materialet på jorden", isTrue: false, explanation: "Wutzitt og lonsdaleitt (begge former for karbon) kan være hardere enn diamant." },
  { statement: "Det er varmere på Venus enn på Merkur", isTrue: true, explanation: "Selv om Merkur er nærmere solen, er Venus varmere på grunn av drivhuseffekten." },
  { statement: "Antibiotika virker mot virus", isTrue: false, explanation: "Antibiotika virker kun mot bakterier, ikke virus. Det er derfor de ikke hjelper mot forkjølelse eller influensa." },
  { statement: "Det tar 7 år å fordøye tyggegummi", isTrue: false, explanation: "Kroppen kan ikke fordøye tyggegummi, men det passerer gjennom systemet på noen få dager." },
  { statement: "Gravitasjonen på månen er 1/6 av jordens gravitasjon", isTrue: true, explanation: "Månens gravitasjon er ca. 16,5% av jordens, ofte avrundet til 1/6." },
  
  // Dyre-påstander  
  { statement: "Struts gjemmer hodet i sanden når de er redde", isTrue: false, explanation: "Strutser gjør ikke dette. De kan løpe opptil 70 km/t når de er i fare." },
  { statement: "Flaggermus er blinde", isTrue: false, explanation: "De fleste flaggermus kan faktisk se ganske godt, i tillegg til å bruke ekkolokalisering." },
  { statement: "Elefanter er redde for mus", isTrue: false, explanation: "Det er ingen vitenskapelig bevis for at elefanter er redde for mus." },
  { statement: "Pandas er ikke bjørn", isTrue: false, explanation: "Store pandas tilhører bjørnefamilien, selv om de var omdiskuterte tidligere." },
  { statement: "Humler kan teoretisk sett ikke fly", isTrue: false, explanation: "Denne myten oppsto fra en misforståelse av aerodynamikk. Humler kan absolutt fly." },
  
  // Historiske påstander
  { statement: "Napoleon var uvanlig kort for sin tid", isTrue: false, explanation: "Napoleon var ca. 170 cm, som var gjennomsnittlig høyde for menn på 1700-tallet." },
  { statement: "Middelalderfolk trodde jorden var flat", isTrue: false, explanation: "De fleste utdannede mennesker i middelalderen visste at jorden var rund." },
  { statement: "Columbus beviste at jorden var rund", isTrue: false, explanation: "Grekerne visste at jorden var rund flere hundre år før Kolumbus." },

 // Sanne påstander
  { statement: "Flamingoer får sin rosa farge fra maten de spiser", isTrue: true,
    explanation: "Fargen kommer fra karotenoider i krepsdyr og alger i kostholdet, ikke fra selve fjærveksten." },

  { statement: "Mennesker og sjimpanser deler omtrent 98-99% av DNA-et sitt", isTrue: true,
    explanation: "Genetiske studier viser at sjimpanser er våre nærmeste slektninger med svært høy DNA-likhet (omtrent 98-99%)." },

  { statement: "Vann utvider seg når det fryser", isTrue: true,
    explanation: "Is har lavere tetthet enn flytende vann fordi molekylstrukturen danner et gitter, derfor flyter is på vann." },

  { statement: "Solen er en stjerne", isTrue: true,
    explanation: "Solen er en mediumstor stjerne (en gul dverg) og fungerer som vårt solsystems primære energikilde." },

  { statement: "Jupiter er den største planeten i solsystemet vårt", isTrue: true,
    explanation: "Jupiter har størst masse og volum av planetene i solsystemet." },

  { statement: "Lyn kan være varmere enn solens overflate", isTrue: true,
    explanation: "Temperaturen i et lyn kan nå titusenvis av grader Celsius — høyere enn solens fotosfære (~5 800 °C)." },

  { statement: "Kameler lagrer fett i puklene, ikke vann", isTrue: true,
    explanation: "Puklene består hovedsakelig av fett; dette gir energireserver. Kameler er likevel svært tilpasset vannlagring og -bruk." },

  { statement: "Månen har i praksis ingen atmosfære", isTrue: true,
    explanation: "Månen har bare en svært tynn eksosfære, så den har ikke en betydelig atmosfære som jorden." },

  { statement: "Pluto er klassifisert som en dvergplanet", isTrue: true,
    explanation: "Siden 2006 har Den internasjonale astronomiske union (IAU) klassifisert Pluto som en dvergplanet." },

  { statement: "Ozonlaget beskytter jorden mot skadelig UV-stråling", isTrue: true,
    explanation: "Ozon (O3) i stratosfæren absorberer store deler av solens ultrafiolette stråling og beskytter levende organismer." },

  { statement: "Blod er rødt på grunn av hemoglobin", isTrue: true,
    explanation: "Hemoglobin i røde blodlegemer inneholder jern og gir blodet den karakteristiske røde fargen når det binder oksygen." },

  { statement: "Noen stener kan flyte på vann", isTrue: true,
    explanation: "Pumice (stein fra vulkansk lava med mye luft) kan være så porøs at den flyter." },

  // Falske påstander / myter
  { statement: "Einstein strøk i matematikk som barn", isTrue: false,
    explanation: "Dette er en myte — Einstein var god i matematikk allerede som barn og mestret avanserte emner tidlig." },

  { statement: "Sukker gjør barn hyperaktive", isTrue: false,
    explanation: "Studier har ikke funnet en direkte sammenheng mellom sukkerinntak og varig hyperaktivitet hos barn." },

  { statement: "Bananer vokser på trær", isTrue: false,
    explanation: "Bananklassifiseres botanisk som en urt; planten er en stor urtaktig vekst, ikke et tre med klart ved." },

  { statement: "Stegosaurus og Tyrannosaurus rex levde samtidig", isTrue: false,
    explanation: "De levde i ulike perioder i mesozoikum — stegosaurus i juratiden, T. rex i krittiden, millioner av år fra hverandre." },

  { statement: "Man bruker enten 'venstre' eller 'høyre' hjernehalvdel mest", isTrue: false,
    explanation: "Selv om enkelte funksjoner er lateraliserte, samarbeider begge hjernehalvdeler i de fleste oppgaver." },

  { statement: "5G-nettet forårsaket COVID-19", isTrue: false,
    explanation: "Det finnes ingen vitenskapelig støtte for at mobilnettverk kan forårsake virussykdommer; COVID-19 er forårsaket av SARS-CoV-2-virus." },

    { statement: "Ketchup gjør at hår vokser raskere", isTrue: false,
    explanation: "Det finnes ingen vitenskapelig sammenheng mellom å smøre ketchup i hodebunnen og økt hårvekst." },

  { statement: "Humler kan ikke fly i motvind", isTrue: false,
    explanation: "Humler kan fly i ulike vindforhold; gamle beregninger som påsto de 'ikke kunne fly' var basert på feil eller forenklede modeller." },

  { statement: "Antibiotika er like effektive mot alle bakterier", isTrue: false,
    explanation: "Ulike antibiotika virker på ulike bakterier; resistensproblemer gjør også at ikke alle antibiotika virker mot alle bakterier." },

      // Sanne påstander
  { statement: "Tomater er en frukt", isTrue: true,
    explanation: "Botanisk regnes tomaten som en frukt (utviklet fra blomstens fruktknute), men mange regner den som en grønnsak." },

  { statement: "Noen frosker kan fryse helt og overleve", isTrue: true,
    explanation: "Arter som 'wood frog' kan tåle at kroppsvæsker delvis fryser ved å bruke sukkerarter som cryoprotektanter som beskytter cellene." },

  { statement: "En gruppe kråker kalles 'murder' på engelsk", isTrue: true,
    explanation: "Det engelske kollektive navnet for kråker er 'a murder of crows' — et gammelt, fargerikt uttrykk." },

  { statement: "Noen skilpadder kan 'puste' gjennom baken", isTrue: true,
    explanation: "Visse arter (f.eks. noen australske ferskvannsskilpadder) kan ta opp oksygen gjennom kloca (rompa)" },

  { statement: "Det finnes flere trær på jorden enn stjerner i Melkeveien", isTrue: true,
    explanation: "Man regner med at det finnes ~3 billioner (3000 milliarder) trær på jorden, mot 100-400 milliarder stjerner i Melkeveien — altså flere trær enn stjerner." },

  { statement: "Bananer er radioaktive", isTrue: true,
    explanation: "Bananer inneholder kalium, inkl. isotopen K-40, som gir et svært lavt nivå av naturlig radioaktivitet." },

  { statement: "Cleopatra levde nærmere månelandingen enn byggingen av de store pyramidene", isTrue: true,
    explanation: "Keopspyramiden ble bygd ~2560 f.Kr.; Cleopatra døde rundt 30 f.Kr.; månelandingen var 1969 e.Kr. Cleopatra er dermed nærmere i tid til 1969 enn til 2560 f.Kr." },

  { statement: "Venus roterer 'baklengs' sammenlignet med jorden", isTrue: true,
    explanation: "Venus har retrograd rotasjon — den snurrer i motsatt retning av de fleste planetene i solsystemet." },

  // Falske påstander / myter
  { statement: "Du svelger gjennomsnittlig åtte edderkopper i søvne hvert år", isTrue: false,
    explanation: "Dette er en populær myte uten bevis — edderkopper unngår vanligvis mennesker." },

  { statement: "Padder/frosker gir deg vorter", isTrue: false,
    explanation: "Menneskelige vorter skyldes virus (HPV). Berøring av padder/rovdyr gir ikke vorter." },

  { statement: "Lemminger begår masse­selvmord ved å hoppe i havet", isTrue: false,
    explanation: "Dette er en myte popularisert av feilaktige framstillinger; lemminger migrerer, men kaster seg ikke kollektivt i havet med vilje." },
];

function FactMyth() {
  const [currentStatement, setCurrentStatement] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [usedStatements, setUsedStatements] = useState([]);

  function getNewStatement() {
    const availableStatements = usedStatements.length >= FACT_MYTH_STATEMENTS.length
      ? FACT_MYTH_STATEMENTS
      : FACT_MYTH_STATEMENTS.filter(s => !usedStatements.find(used => used.statement === s.statement));
    const statement = availableStatements[randInt(0, availableStatements.length - 1)];
    setCurrentStatement(statement);
    setShowAnswer(false);
    if (usedStatements.length >= FACT_MYTH_STATEMENTS.length) {
      setUsedStatements([statement]);
    } else {
      setUsedStatements([...usedStatements, statement]);
    }
  }

  function handleAnswer(answer) {
    setShowAnswer(true);
    if (answer === currentStatement.isTrue) beep(300, 1200); else beep(400, 800);
  }

  if (!currentStatement) {
    return (
      <IntroScreen
        icon="🤔" name="Fakta eller myte"
        sentence="Er påstanden sann eller oppspinn?"
        primaryLabel="Vis påstand" onPrimary={getNewStatement} showDraw
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '40px', width: '100%', maxWidth: '900px' }}>
      <div aria-live="polite" style={{
        fontSize: 'clamp(32px, 4.5vw, 60px)', fontWeight: 800, fontFamily: FONT_DISPLAY,
        textAlign: 'center', lineHeight: 1.15, color: COLOR.textPrimary
      }}>«{currentStatement?.statement}»</div>

      <div style={{ minHeight: '130px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px', justifyContent: 'center' }}>
        {!showAnswer ? (
          <div style={{ display: 'flex', gap: '12px' }}>
            <button onClick={() => handleAnswer(true)} style={mainPrimaryBtnStyle()}>Fakta</button>
            <button onClick={() => handleAnswer(false)} style={mainSecondaryBtnStyle()}>Myte</button>
          </div>
        ) : (
          <>
            <div style={{ fontSize: '19px', color: COLOR.textSecondaryOnPanel }}>Svar</div>
            <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '48px', color: COLOR.accentText }}>
              {currentStatement?.isTrue ? "Fakta" : "Myte"}
            </div>
            <div style={{ fontSize: '17px', color: COLOR.textSecondary, maxWidth: '640px', textAlign: 'center', lineHeight: 1.5 }}>
              {currentStatement?.explanation}
            </div>
          </>
        )}
      </div>

      {showAnswer && (
        <button onClick={getNewStatement} style={mainPrimaryBtnStyle()}>Neste påstand</button>
      )}
    </div>
  );
}
